import * as api from '../core/api/api';
import * as appServiceLines from '../core/service-lines';
import {AutomateGeneration, isPathology} from '../core/service-lines';
import * as _cpt from '../core/charges/charge-codes';
import {buildDescriptionRequest} from '../core/charges/charge-codes';
import * as dictionaries from '../core/dictionaries/index';
import HttpMethods from '../enums/http-methods';
import * as commonEnums from '../enums/index';
import * as commonActions from './action-types';
import * as vitalware from '../core/vitalware';
import * as cmxDateTime from '../core/date-time/cmx-date-time';
import * as documentUtils from '../core/document-utils';
import {Dictionary} from '../core/document-utils';
import * as objectUtils from '../core/object-utils';
import {isEmpty} from '../core/object-utils';
import * as conditionalUtils from '../core/amplify/utils/conditional-utilities';
import * as dtoFactory from '../core/amplify/transaction-updates.dto-factory';
import {sequenceGenerator} from '../core/amplify/sequence-generator';
import {amplifyFeedback, hideFeedback} from './ui.actions';
import {createAction, createAsyncThunk} from '@reduxjs/toolkit';
import {fetchActivity, getProcessInstance, updateProcessInstance} from './code-bench-shared.actions';
import {fetchXcaliberToken} from '../core/amplify/middlewares/xcaliber.middleware';
import {searchParamsFactory} from '../core/amplify/utils/pagination-utils';
import {fetchDictionaryRows} from './dictionary-utilities';
import {
  getPatientMessageCountWithFilter,
  getPatientMessageCountWithoutFilter,
  getPatientTimelineWithFilter,
  getPatientTimelineWithoutFilter
} from './directory-utils';
import {
  getDataAccessPatientMessageCountwithFilter,
  getDataAccessPatientMessageCountWithoutFilter,
  getDataAccessPatientTimelineCaseWithoutFilter,
  getDataAccessPatientTimelinewithFilter
} from './data-access-service-utils';
import {getCodeTypeShortName} from '../core/case/case-utils';
import {CoderNotesLookupTypeName} from '../enums';
import {getRelatedCasesFilterRequest} from '../core/amplify/utils/common-filter-utils';
import {shouldAutoAdvanceWorklistAfterSubmit} from '../core/amplify/utils/cursor-worklist-navigation';
import {buildDataAccessRequestObject, shouldFetchFromDataAccess} from '../core/artifacts/utils';
import {isResourceAllowed} from '../core/amplify/utils/bench-editable';

export {
  fetchActivity,
  fetchCodeBenchLayoutConfiguration,
  updateProcessInstance
} from './code-bench-shared.actions';

const { Bench, InvoiceMetadata, Launch, UserInterface } = commonActions;
const { ProcessInstanceActions, InvoiceTypes, IssueTypeErrors } = commonEnums;

export const clearSearchResults = createAction<string>(Bench.CLEAR_SEARCH);
export const caseOrganization = createAction<CMxAPI.Organization>(
  InvoiceMetadata.ORGANIZATION_AVAILABLE
);
export const codeDescriptionsAvailable = createAction<CMxAPI.CodeResponse>(
  Launch.CODE_DESCRIPTIONS
);

const REQUEST_INIT_OPTIONS: RequestInit = {
  method: 'GET',
  cache: 'no-cache', // *default, no-cache, reload, force-cache, only-if-cached
  redirect: 'follow',
  referrer: 'no-referrer'
};

export const setUnresolvedDocuments = createAction<boolean>(
  Bench.SET_UNRESOLVED_DOCUMENTS
);

export const setActiveMiniTab = createAction<string>(Bench.SET_ACTIVE_MINI_TAB);
export const setActiveVerticalMiniTab = createAction<string>(
  Bench.SET_ACTIVE_VERTICAL_MINI_TAB
);

export const popupNavigate = createAction<Accelerate.CaseNavigate>(
  Bench.POPUP_NAVIGATE
);
export const promptClose = createAction<void>(Bench.PROMPT_CLOSE);
export const popupClose = createAction<void>(Bench.POPUP_CLOSE);

export const optimisticCodeChange = createAction<Accelerate.OptimisticChange>(
  Bench.OPTIMISTIC_CHANGE
);

export const optimisticProTechChange = createAction<
  CMxCommonApp.TechCodableTransaction
>(Bench.OPTIMISTIC_PRO_TECH_CHANGE);

export const featureSelection = createAction<CMxCommonApp.ChargeSessionSelection>(
  Bench.FEATURE_SELECTION
);

const fetchExcludeCasePermissionsConfig = createAsyncThunk<
  Record<string, any>,
  { tenantId: string; serviceLine: string },
  { rejectValue: CMxCommonApp.CMxError }
>(
  'FETCH_EXCLUDE_CASE_PERMISSIONS_CONFIG',
  async ({ tenantId, serviceLine }) => {
    try {
      const response = await fetch(
        `/configuration/detail/exclude-case-permissions/${tenantId}/v2?merge=true&serviceLine=${serviceLine}`,
        {
          method: "GET",
          headers: {
            "Accept": "application/json",
            "Content-Type": "application/json",
            "Authorization": `Bearer ${sessionStorage.getItem("authToken")}`,
          }
        }
      );

      if (!response.ok) {
        console.warn(
          `[fetchExcludeCasePermissionsConfig] HTTP error ${response.status}`
        );
        return {};
      }

      const responseData: any[] = await response.json();

      if (!Array.isArray(responseData) || responseData.length === 0) {
        return {};
      }

      const configObj = responseData[0];
      const mergedValueObj = configObj.values?.find(
        (v: any) => v.value === 'MERGED'
      );

      return mergedValueObj?.jsonValue || {};
    } catch (error) {
      console.error('[fetchExcludeCasePermissionsConfig] Error:', error);
      return {};
    }
  }
);

const cancelInvoicesPopup = createAction<AmplifyAPI.ProcessInstance>(
  Bench.CANCEL_INVOICES_POPUP
);

const openAddChargeModal = createAction<void>(Bench.OPEN_ADD_CHARGE_MODAL);

const closeAddChargeModal = createAction<void>(Bench.CLOSE_ADD_CHARGE_MODAL);

const showAddChargeLoading = createAction<void>(Bench.ADD_TRANSACTION_LOADING);

const toggleSubmitOldCaseModal = createAction<void>(
  Bench.TOGGLE_SUBMIT_OLD_CASE_MODAL
);

const showSideNavBar = createAction<void>(Bench.SHOW_SIDE_NAV_BAR);

const hideSideNavBar = createAction<void>(Bench.HIDE_SIDE_NAV_BAR);

const showSubmittedFeedback = createAction<void>(
  Bench.SHOW_SUBMITTED_FEEDBACK
);

const showLoadingSubmission = createAction<void>(
  Bench.SHOW_LOADING_SUBMISSION
);

const showLoadingCancelation = createAction<void>(
  Bench.SHOW_LOADING_CANCELATION
);

const hideLoadingSubmission = createAction<void>(
  Bench.HIDE_LOADING_SUBMISSION
);

const hideSubmittedFeedback = createAction<void>(
  Bench.HIDE_SUBMITTED_FEEDBACK
);

const showCanceledFeedback = createAction<void>(Bench.SHOW_CANCELED_MODAL);

const showSizeExpWarning = createAction<void>(Bench.SHOW_SIZE_EXP_WARNING);

const showvitalwareIssuesModal = createAction<void>(
  Bench.SHOW_CLAIMS_ISSUES_MODAL
);

const hidevitalwareIssuesModal = createAction<void>(
  Bench.HIDE_CLAIMS_ISSUES_MODAL
);

const setvitalwareIssues = createAction<any>(Bench.SET_CLAIMS_ISSUES);

const hideSizeExpWarning = createAction<void>(Bench.HIDE_SIZE_EXP_WARNING);

const deleteSizeExpWarning = createAction<void>(Bench.DELETE_SIZE_EXP_WARNING);

const showHoldCaseModal = createAction<void>(Bench.SHOW_HOLD_CASE_MODAL);

const hideHoldCaseModal = createAction<void>(Bench.HIDE_HOLD_CASE_MODAL);

const showCaseCommentModal = createAction<void>(Bench.SHOW_CASE_COMMENT_MODAL);

const hideCaseCommentModal = createAction<void>(Bench.HIDE_CASE_COMMENT_MODAL);

const showExclusionCaseModal = createAction<void>(
  Bench.SHOW_EXCLUSION_CASE_MODAL
);

const hideExclusionCaseModal = createAction<void>(
  Bench.HIDE_EXCLUSION_CASE_MODAL
);

const toggleLateCaseClearModal = createAction<boolean>(
  Bench.TOGGLE_LATE_CASE_CLEAR_MODAL
);

const showSplitCaseModal = createAction<void>(Bench.SHOW_SPLIT_CASE_MODAL);

const hideSplitCaseModal = createAction<void>(Bench.HIDE_SPLIT_CASE_MODAL);

const cancelInvoicesDismiss = createAction<void>(
  Bench.CANCEL_INVOICES_DISMISS
);

const selectInvoiceToEdit = createAction<AmplifyAPI.Invoice>(
  Bench.SELECT_INVOICE_TO_EDIT
);

const selectInvoiceToAdd = createAction<AmplifyAPI.Invoice>(
  Bench.SELECT_INVOICE_TO_ADD
);

const submittedFeedbackTimer = createAction<void>(Bench.SUBMITTED_MODAL_TIMER);

const setLoadingTechPro = createAction<{ guid: string; value: boolean }>(
  Bench.SET_LOADING_TECH_PRO
);

const setFilterTypes = createAction<any[]>(
  Bench.SET_FILTER_TYPES_PATIENT_TIMELINE
);

const setFilterTypesInfinite = createAction<any[]>(
  Bench.SET_FILTER_TYPES_PATIENT_TIMELINE_INFINITE
);

const setFilterDates = createAction<CMxCommonApp.TimelineDateFilter>(
  Bench.SET_FILTER_DATES_PATIENT_TIMELINE_INFINITE
);

const setFilterDatesInfinite = createAction<CMxCommonApp.TimelineDateFilter>(
  Bench.SET_FILTER_DATES_PATIENT_TIMELINE
);

const updateTransaction = createAction<{
  transaction: AmplifyAPI.Transaction;
  invoiceGuid: string;
}>(Bench.UPDATE_TRANSACTION);

const updateTransactionOptimistically = createAction<{
  addDTO: any;
  invoiceGuid: string;
}>(Bench.UPDATE_TRANSACTION_OPTIMISTICALLY);

const createTransactionOptimistically = createAction<{
  createDTO: any;
  invoiceGuid: string;
}>(Bench.CREATE_TRANSACTION_OPTIMISTICALLY);

const replaceOptimisticTransaction = createAction<{
  transaction: any;
  invoiceGuid: string;
}>(Bench.REPLACE_OPTIMISTIC_TRANSACTION);

const docMetadataAvailable = createAction<CMxAPI.DocumentMetadata[]>(
  Bench.DOCUMENT_METADATA
);

const setActiveNote = createAction<CMxCommonApp.DecoratedNote>(
  Bench.SET_ACTIVE_NOTE
);

const updateRelatedCasesFilterSelection = createAction<
  CMxCommonApp.FilterSelection
>(Bench.UPDATE_RELATED_CASE_FILTER_SELECTION);

const getCodeChangeMeta = (codeChange: Accelerate.CodeActionContextual) => {
  const {
    context: { activeItem, chargeSession, invoice, processInstance }
  } = codeChange;
  const serviceLineConfig = appServiceLines.determineServiceLineConfig({
    name: processInstance.serviceLine
  });
  const processInstanceId = `${processInstance}`;

  return { serviceLine: serviceLineConfig.amplifyPath, processInstanceId };
};

const getCodeChangeURL = (codeChange: Accelerate.CodeActionContextual) => {
  const { processInstance, invoice } = codeChange.context;
  const { serviceLine } = getCodeChangeMeta(codeChange);
  if (invoice.type === InvoiceTypes.SHARED_CODES) {
    return `/coding-api/processinstance/${serviceLine}/${processInstance.guid.uuid}/codes/shared/v1`;
  }
  return `/coding-api/processinstance/${serviceLine}/${processInstance.guid.uuid}/codes/v1`;
};

const getTransactionURLForNewInvoice = (
  newInvoiceData: Accelerate.NewInvoice
) => {
  const { activeItem, processInstanceId, invoiceGuid } = newInvoiceData;
  const serviceLineConfig = appServiceLines.determineServiceLineConfig({
    name: activeItem.service_line
  });
  return `/coding-api/processinstance/${serviceLineConfig.amplifyPath}/${processInstanceId}/transaction/v1?invoiceGuid=${invoiceGuid}`;
};

const getInvoiceURL = (codeChange: Accelerate.CodeActionContextual) => {
  const { processInstance } = codeChange.context;
  const { serviceLine } = getCodeChangeMeta(codeChange);
  return `/coding-api/processinstance/${serviceLine}/${processInstance.guid.uuid}/v1?evidence=true&errors=true`;
};

const getCreateTransactionAndCodeURL = (
  codeChange: Accelerate.CodeActionContextual
) => {
  const { processInstance } = codeChange.context;
  const { serviceLine } = getCodeChangeMeta(codeChange);
  return `/coding-api/processinstance/${serviceLine}/${processInstance.guid.uuid}/transactionwithcode/v1`;
};

const getSharedActionURL = (
  codeChange: CMxCommonApp.TransactionSharedCodeChange
) => {
  const {
    codeUpdate: { diagnoses, transactionGuid, serviceLine, processInstance }
  } = codeChange;
  const serviceLineConfig = appServiceLines.determineServiceLineConfig({
    name: processInstance.serviceLine
  });
  return `/coding-api/processinstance/${serviceLineConfig.amplifyPath}/${processInstance.guid.uuid}/codes/v1`;
};

type DescriptionRequestArguments = {
  invoices: AmplifyAPI.Invoice[];
  caseRunTenantName: string;
  caseRunDateOfService: string;
};

const cancelInvoicesConfirm = createAsyncThunk<
  AmplifyAPI.ProcessInstance,
  AmplifyAPI.Submission,
  { rejectValue: any }
>(Bench.CANCEL_INVOICES_CONFIRM, async (submission, { dispatch }) => {
  const { processInstance } = submission;
  const uuid = processInstance.guid.uuid;
  const serviceLineConfig = appServiceLines.determineServiceLineConfig({
    name: processInstance.serviceLine
  });
  const endpoint = `/coding-api/processinstance/${serviceLineConfig.amplifyPath}/${uuid}/cancel/v1`;

  (dispatch as any)(hideFeedback());
  dispatch(showLoadingCancelation());

  await api.request(endpoint, {
    ...REQUEST_INIT_OPTIONS,
    method: HttpMethods.POST
  });

  dispatch(showCanceledFeedback());

  let updateProcessInstance = await api.requestJSON<AmplifyAPI.ProcessInstance>(
    `/coding-api/processinstance/${serviceLineConfig.amplifyPath}/${uuid}/v1?evidence=true&errors=true`,
    { ...REQUEST_INIT_OPTIONS }
  );

  dispatch(fetchActivity(updateProcessInstance) as any);

  if (submission.uiMode === commonEnums.UIModesEnum.EPIC_EMBEDDED) {
    dispatch(showCanceledFeedback());
  }
  if (shouldAutoAdvanceWorklistAfterSubmit(submission.cursorAPIPayload)) {
    await dispatch(
      loadNextOrPreviousCase({
        cursorAPIPayload: submission.cursorAPIPayload,
        status: commonEnums.FeedbackMeta.SUCCESS
      })
    );
  }
  return updateProcessInstance;
});

const fetchCodeDescriptions = createAsyncThunk<
  any,
  DescriptionRequestArguments,
  { rejectValue: any }
>(Bench.CODE_DESCRIPTIONS, async (req, { dispatch }) => {
  const { caseRunTenantName, caseRunDateOfService, invoices } = req;
  const tenantInfo = await api.requestJSON<CMxAPI.Organization>(
    `/organization/by-name/${caseRunTenantName}/v1`
  );
  dispatch(caseOrganization(tenantInfo));

  const url = `/dictionary/codes/description/v2?tenantId=${tenantInfo.tenantId}`;
  const descriptionRequest = buildDescriptionRequest(
    invoices,
    caseRunDateOfService
  );

  if (descriptionRequest.length === 0) {
    return {};
  }

  const descriptions = await api.requestJSON<CMxAPI.CodeResponse>(url, {
    ...REQUEST_INIT_OPTIONS,
    method: HttpMethods.POST,
    body: JSON.stringify(descriptionRequest)
  });
  const normalizeDescriptions = dictionaries.normalizeDescriptions(
    descriptions
  );

  (dispatch as any)(codeDescriptionsAvailable(normalizeDescriptions));
  return normalizeDescriptions;
});

const fetchAllInvoices = createAsyncThunk<
  AmplifyAPI.InvoiceOptionsDTO,
  AmplifyAPI.ProcessInstance,
  { rejectValue: CMxCommonApp.CMxError }
>(
  ProcessInstanceActions.ALL_INVOICES,
  async (processInstance: AmplifyAPI.ProcessInstance) => {
    const url = `/coding-api/processinstance/${processInstance.serviceLine}/${processInstance.guid.uuid}/invoice/v1`;
    const invoices = await api.requestJSON<AmplifyAPI.InvoiceOptionsDTO>(url, {
      ...REQUEST_INIT_OPTIONS
    });
    return invoices;
  }
);

const fetchProcessInstance = createAsyncThunk<
  AmplifyAPI.ProcessInstance,
  CMxAPI.CaseRun,
  { rejectValue: CMxCommonApp.CMxError }
>(ProcessInstanceActions.INVOICE_AVAILABLE, async (caseRun, { dispatch, getState }) => {
  const serviceLineConfig = appServiceLines.determineServiceLineConfig({
    name: caseRun.service_line
  });

  const processInstance = await getProcessInstance(
    `/coding-api/processinstance/${serviceLineConfig.amplifyPath}/${caseRun.process_instance_guid}/v1?evidence=true&errors=true`
  );

  dispatch(fetchActivity(processInstance) as any);

  dispatch(
    fetchCodeDescriptions({
      invoices: processInstance.invoices,
      caseRunTenantName: caseRun.tenant_name,
      caseRunDateOfService: caseRun.date_of_service
    }) as any
  );
  dispatch(fetchAllInvoices(processInstance) as any);
  const { getImprovementsForProcessInstance } = await import('./improvements.actions');
  dispatch(getImprovementsForProcessInstance(processInstance) as any);

  if (processInstance.caseType === commonEnums.UIModesEnum.STANDALONE) {
    try {
      await dispatch(getHoldCaseForProcessInstance(processInstance) as any);
    } catch (error) {
      console.error(`Error getting holding case information: ${error.message}`);
    }
  }

  await dispatch(getCaseVersions(processInstance) as any);

  dispatch(
    fetchSourceInvoices({
      processInstanceId: processInstance.guid.uuid,
      serviceLine: processInstance.serviceLine,
      caseRunTenantName: caseRun.tenant_name,
      caseRunDateOfService: caseRun.date_of_service
    }) as any
  );

  // Fetch exclude case permissions config
  const state = getState() as any;
  const tenantId = state.capabilities?.userContext?.tenantId;
  if (tenantId && caseRun.service_line) {
    try {
      await dispatch(
        fetchExcludeCasePermissionsConfig({
          tenantId,
          serviceLine: caseRun.service_line
        }) as any
      );
    } catch (error) {
      console.warn('[fetchProcessInstance] Failed to fetch exclude case permissions config:', error);
    }
  }

  return processInstance;
});


const fetchSourceInvoices = createAsyncThunk<
  AmplifyAPI.Invoice[],
  {
    processInstanceId: string;
    serviceLine?: string;
    caseRunTenantName: string;
    caseRunDateOfService: string;
  },
  { rejectValue: CMxCommonApp.CMxError }
>(
  ProcessInstanceActions.FETCH_SOURCE_INVOICES,
  async (
    { processInstanceId, serviceLine, caseRunTenantName, caseRunDateOfService },
    { dispatch }
  ) => {
    const params = new URLSearchParams();

    const invoiceTypes = [
      InvoiceTypes.ASSEMBLED,
      InvoiceTypes.MAPPED,
      InvoiceTypes.CLINICAL
    ];
    if (invoiceTypes?.length) {
      params.append('InvoiceTypes', invoiceTypes.join(','));
    }

    if (serviceLine) {
      params.append('ServiceLine', serviceLine);
    }

    const url = `/coding-api/processinstance/${serviceLine}/${processInstanceId}/invoices/v1${
      params.toString() ? `?${params.toString()}` : ''
    }`;
    const invoices = await api.requestJSON<AmplifyAPI.Invoice[]>(url, {
      ...REQUEST_INIT_OPTIONS
    });

    dispatch(
      fetchCodeDescriptions({
        invoices,
        caseRunTenantName: caseRunTenantName,
        caseRunDateOfService: caseRunDateOfService
      }) as any
    );
    return invoices;
  }
);

const submitInvoice = createAsyncThunk<
  AmplifyAPI.ProcessInstance,
  AmplifyAPI.Submission,
  { rejectValue: CMxCommonApp.CMxError }
>(
  ProcessInstanceActions.INVOICE_SUBMITTED,
  async (submission, { dispatch }) => {
    const {
      processInstance,
      cursorAPIPayload,
      submitWithOverrideButton,
      caseAccessTime,
      activeItem
    } = submission;

    const serviceLineConfig = appServiceLines.determineServiceLineConfig({
      name: processInstance.serviceLine
    });
    const processInstanceId = `${processInstance.guid.uuid}`;
    let errorList: AmplifyAPI.AmplifyError[] = [];

    (dispatch as any)(hideFeedback());
    dispatch(showLoadingSubmission());

    const initData: any = { ...REQUEST_INIT_OPTIONS, method: HttpMethods.POST };
    let submitBody: any = {};

    if (!!!submitWithOverrideButton && !!caseAccessTime) {
      submitBody = {
        look_back_period: caseAccessTime
      };
    } else if (!!submitWithOverrideButton && submission.vwComment !== null) {
      submitBody = {
        ...submitBody,
        override_message: submission.vwComment
      };
    }

    if (
      submission?.removeHold !== null &&
      submission?.removeHold !== undefined
    ) {
      submitBody = {
        ...submitBody,
        remove_hold: submission.removeHold
      };
    }
    if (!isEmpty(submitBody)) {
      initData.body = JSON.stringify(submitBody);
    }

    try {
      await api.request(
        `/coding-api/processinstance/${serviceLineConfig.amplifyPath}/${processInstanceId}/submit/v1`,
        initData
      );

      dispatch(showSubmittedFeedback());
      if (shouldAutoAdvanceWorklistAfterSubmit(cursorAPIPayload)) {
        await dispatch(
          loadNextOrPreviousCase({
            cursorAPIPayload: cursorAPIPayload,
            status: commonEnums.FeedbackMeta.SUCCESS
          })
        );
      }
    } catch (error) {
      const unexpectedBadRequest =
        typeof error?.errors === 'object' && 'errors' in error?.errors;
      dispatch(hideLoadingSubmission());
      if (!unexpectedBadRequest) {
        errorList = error?.errors || [];
        if (errorList.length > 0) {
          (dispatch as any)(
            amplifyFeedback({
              message: 'There were issues submitting this case.',
              meta: commonEnums.FeedbackMeta.ERROR,
              dismissable: true
            })
          );
        } else {
          (dispatch as any)(
            amplifyFeedback({
              message: 'There was en error submitting this case.',
              meta: commonEnums.FeedbackMeta.ERROR,
              dismissable: true
            })
          );
        }

        // claim and cci errors implementation
        const { claims, cci, custom_rules } = vitalware.parseInvoiceIssues(
          error
        );
        const standardErrors = vitalware.filterInvoiceIssues(error);
        const mergeHistory = vitalware.filterMergeHistoryWarnings(error);

        if (
          standardErrors.length === 0 &&
          (cci.length > 0 ||
            claims.length > 0 ||
            custom_rules.length > 0 ||
            mergeHistory.length > 0)
        ) {
          dispatch(showvitalwareIssuesModal());
        }
        if (shouldAutoAdvanceWorklistAfterSubmit(cursorAPIPayload)) {
          await dispatch(
            loadNextOrPreviousCase({
              cursorAPIPayload: cursorAPIPayload,
              status: commonEnums.FeedbackMeta.ERROR
            })
          );
        }
      } else {
        const nonEditableMessage = 'Instance cannot be edited in current state';
        const badRequestErrors = error?.errors;
        if (
          typeof badRequestErrors === 'object' &&
          'message' in badRequestErrors &&
          badRequestErrors?.message === nonEditableMessage
        ) {
          (dispatch as any)(
            amplifyFeedback({
              message:
                'Changes may have occurred to the case. Unable to proceed with Submit',
              meta: commonEnums.FeedbackMeta.ERROR,
              dismissable: true
            })
          );
        } else {
          (dispatch as any)(
            amplifyFeedback({
              message: 'There was en error submitting this case.',
              meta: commonEnums.FeedbackMeta.ERROR,
              dismissable: true
            })
          );
        }
      }

      // claim and cci errors implementation
      const {
        claims,
        cci,
        custom_rules,
        unresolved_documents,
        hold_issue
      } = vitalware.parseInvoiceIssues(error);
      const standardErrors = vitalware.filterInvoiceIssues(error);

      if (
        standardErrors.length === 0 &&
        (cci.length > 0 ||
          claims.length > 0 ||
          custom_rules.length > 0 ||
          unresolved_documents.length > 0 ||
          hold_issue.length > 0)
      ) {
        dispatch(showvitalwareIssuesModal());
      }
      if (shouldAutoAdvanceWorklistAfterSubmit(cursorAPIPayload)) {
        await dispatch(
          loadNextOrPreviousCase({
            cursorAPIPayload: cursorAPIPayload,
            status: commonEnums.FeedbackMeta.ERROR
          })
        );
      }
    }
    let updatedProcessInstance = await api.requestJSON<
      AmplifyAPI.ProcessInstance
    >(
      `/coding-api/processinstance/${serviceLineConfig.amplifyPath}/${processInstanceId}/v1?evidence=true&errors=true`,
      {
        ...REQUEST_INIT_OPTIONS
      }
    );

    dispatch(fetchActivity(updatedProcessInstance) as any);
    if (!isPathology(activeItem?.service_line)) {
      dispatch(
        getCCICustomClaimCrossCases({
          activeItem: activeItem,
          processInstance: updatedProcessInstance
        }) as any
      );
    }

    return updatedProcessInstance;
  }
);

// -----------------------------
// Edit calls.  There's a pattern with the edit calls
// where each edit executes and then the invoices structure
// is retrieved from the back end.
// -----------------------------
const codeSearch = createAsyncThunk<
  Accelerate.CodeSearchResponse,
  Accelerate.CodeQuery,
  { rejectValue: CMxCommonApp.CMxError }
>(Bench.CODE_SEARCH, async codeQuery => {
  const effectiveDate =
    codeQuery.context.activeItem.date_of_service ?? '2020-01-01';
  const searchId = codeQuery.context?.chargeSession?.transactionId ?? '';
  const tenantId = process.env.REACT_APP_DICT_TENANT_OVERRIDE ?? codeQuery.context?.userContext?.tenantId ?? '';

  // Code search is not canceled by fetch abort signals.  As this is a type-ahead
  // the responses in flight, may still be valuable if they aren't the latest
  // REQUEST.  That said, we only want to show the latest RESPONSE.
  // There is caching in the reducer, where the responses will be registered.
  // The action is merely responsible for appending some metadata on the search.
  const response: Accelerate.CodeSearchResponse = {
    codeQuery,
    searchResults: [],
    meta: {
      searchId: searchId,
      sequenceNumber: sequenceGenerator.next(searchId)
    }
  };

  const searchMods =
    codeQuery.text.length > 1 &&
    codeQuery.searchType ===
      `${commonEnums.ChargeSessionCodeType.MODIFIER}`.toUpperCase();
  const meetsIMOMin = codeQuery.text.length > 2;

  if (searchMods || meetsIMOMin) {
    const lookupParams = new URLSearchParams([['tenantId', tenantId]]);

    const searchResponse = await api.requestJSON<
      CMxAPI.PageableList<CMxCommonApp.CodeInfo>
    >(`/dictionary/code/v2?${lookupParams.toString()}`, {
      method: HttpMethods.POST,
      body: JSON.stringify({
        searchTerm: codeQuery.text.toUpperCase(),
        codeType: `${codeQuery.searchType}`.toUpperCase(),
        effectiveDate
      })
    });
    response.searchResults = searchResponse.content;
  }

  return response;
});

const txnSharedCodeAction = createAsyncThunk<
  AmplifyAPI.ProcessInstance,
  CMxCommonApp.TransactionSharedCodeChange,
  { rejectValue: CMxCommonApp.CMxError }
>(Bench.TXN_SHARED_CODE, async (txnSharedCodeChange, { dispatch }) => {
  const {
    action,
    codeUpdate: { diagnoses, transactionGuid, serviceLine, processInstance }
  } = txnSharedCodeChange;

  const serviceLineConfig = appServiceLines.determineServiceLineConfig({
    name: processInstance.serviceLine
  });
  const updateURL = getSharedActionURL(txnSharedCodeChange);

  const method =
    action === 'REMOVE'
      ? HttpMethods.DELETE
      : action === 'ADD'
      ? HttpMethods.POST
      : undefined;

  if (method) {
    await api.requestJSON<AmplifyAPI.Transaction>(updateURL, {
      ...REQUEST_INIT_OPTIONS,
      method,
      body: JSON.stringify({
        transactionGuid,
        diagnoses
      })
    });
  }

  let updatedProcessInstance = await api.requestJSON<
    AmplifyAPI.ProcessInstance
  >(
    `/coding-api/processinstance/${serviceLineConfig.amplifyPath}/${processInstance.guid.uuid}/v1?evidence=true&errors=true`,
    {
      ...REQUEST_INIT_OPTIONS
    }
  );

  dispatch(fetchActivity(updatedProcessInstance) as any);

  return updatedProcessInstance;
});

const codeUpdateFlow = async (
  codeChange: Accelerate.CodeChange,
  dispatch: any
): Promise<AmplifyAPI.ProcessInstance> => {
  const transactionURL = getCodeChangeURL(codeChange);
  const invoiceURL = getInvoiceURL(codeChange);

  // run the codeChange through the factory to get the submission
  // then we'll optimistically set the data to that submission
  // if the action fails we'll be rever to the earlier version.
  const updateDTO = dtoFactory.updateDTO(codeChange);

  //We should remove these if they are not used
  dispatch(
    optimisticCodeChange({
      updateDTO,
      codeChange
    })
  );

  await api.request(transactionURL, {
    ...REQUEST_INIT_OPTIONS,
    method: HttpMethods.PUT,
    body: JSON.stringify(updateDTO)
  });

  const processInstance = await api.requestJSON<AmplifyAPI.ProcessInstance>(
    invoiceURL,
    {
      ...REQUEST_INIT_OPTIONS
    }
  );

  dispatch(fetchActivity(processInstance) as any);

  return processInstance;
};

const codeUpdate = createAsyncThunk<
  AmplifyAPI.ProcessInstance,
  Accelerate.CodeChange,
  { rejectValue: CMxCommonApp.CMxError }
>(Bench.CODE_UPDATE, (codeChange, { dispatch }) => codeUpdateFlow(codeChange, dispatch));

const codeRemove = createAsyncThunk<
  AmplifyAPI.ProcessInstance,
  Accelerate.CodeChange,
  { rejectValue: CMxCommonApp.CMxError }
>(Bench.CODE_REMOVE, async (codeChange, { dispatch }) => {
  const transactionURL = getCodeChangeURL(codeChange);
  const invoiceURL = getInvoiceURL(codeChange);

  const {
    context: { invoice, activeItem }
  } = codeChange;
  let removeCode: any;

  if (invoice.type === InvoiceTypes.SHARED_CODES) {
    const diagnosis_code = codeChange.codes[0].code;
    removeCode = { diagnosis_code: diagnosis_code };
  } else {
    removeCode = dtoFactory.deleteDTO(codeChange);
    dispatch(
      optimisticCodeChange({
        updateDTO: removeCode,
        codeChange
      })
    );
  }

  await api.request(transactionURL, {
    ...REQUEST_INIT_OPTIONS,
    method: HttpMethods.DELETE,
    body: JSON.stringify(removeCode)
  });

  const processInstance = await api.requestJSON<AmplifyAPI.ProcessInstance>(
    invoiceURL,
    {
      ...REQUEST_INIT_OPTIONS
    }
  );

  dispatch(
    fetchRelatedCodes({
      encounterId: activeItem?.encounter_identifier?.number,
      serviceLine: activeItem?.service_line,
      codeType: getCodeTypeShortName(codeChange?.codes?.[0]?.codeType),
      caseId: activeItem?.case_id
    }) as any
  );

  dispatch(fetchActivity(processInstance) as any);

  return processInstance;
});

const codesRemove = createAsyncThunk<
  AmplifyAPI.ProcessInstance,
  Accelerate.CodeChange,
  { rejectValue: CMxCommonApp.CMxError }
>(Bench.BULK_CODE_REMOVE, async (codeChange, { dispatch }) => {
  const transactionURL = getCodeChangeURL(codeChange);
  const invoiceURL = getInvoiceURL(codeChange);

  const {
    context: { invoice, activeItem }
  } = codeChange;

  if (invoice.type === InvoiceTypes.SHARED_CODES) {
    for (const code of codeChange.codes) {
      await api.request(transactionURL, {
        ...REQUEST_INIT_OPTIONS,
        method: HttpMethods.DELETE,
        body: JSON.stringify({ diagnosis_code: code.code })
      });
    }
  } else {
    const removeCode = dtoFactory.BulkDeleteDTO(codeChange);
    dispatch(
      optimisticCodeChange({
        updateDTO: removeCode,
        codeChange
      })
    );

    await api.request(transactionURL, {
      ...REQUEST_INIT_OPTIONS,
      method: HttpMethods.DELETE,
      body: JSON.stringify(removeCode)
    });
  }

  const processInstance = await api.requestJSON<AmplifyAPI.ProcessInstance>(
    invoiceURL,
    {
      ...REQUEST_INIT_OPTIONS
    }
  );

  dispatch(
    fetchRelatedCodes({
      encounterId: activeItem?.encounter_identifier?.number,
      serviceLine: activeItem?.service_line,
      codeType: getCodeTypeShortName(codeChange?.codes?.[0]?.codeType),
      caseId: activeItem?.case_id
    }) as any
  );

  dispatch(fetchActivity(processInstance) as any);

  return processInstance;
});

const timeBasedCodeAdd = createAsyncThunk<
  AmplifyAPI.ProcessInstance,
  { codeChange: Accelerate.CodeChange; time: string; age: string },
  { rejectValue: CMxCommonApp.CMxError }
>(Bench.TIME_BASED_CODE_ADD, async ({ codeChange, time, age }, { dispatch }) => {
  const invoiceURL = getInvoiceURL(codeChange);
  const { processInstance } = codeChange.context;
  const { serviceLine } = getCodeChangeMeta(codeChange);
  const transaction = codeChange?.context?.transaction;
  const addTimeBasedCode = {
    em_code: transaction?.procedure?.procedure_code,
    total_time: time,
    age: age,
    medicare_time_rules: transaction?.procedure?.medicare_time_rules,
    metadata: {
      transaction_id: transaction?.guid
    }
  };
  // Capture result while generating the code with time.
  const addTimeBasedCodeResponse = await api.request(
    `/coding-api/processinstance/${serviceLine}/${processInstance.guid.uuid}/timebasedcode/v1`,
    {
      ...REQUEST_INIT_OPTIONS,
      method: HttpMethods.POST,
      body: JSON.stringify(addTimeBasedCode)
    }
  );
  const addTimeBasedCodeResponseData = await addTimeBasedCodeResponse.json();
  const addOnCodeResponse = addTimeBasedCodeResponseData?.add_on_code;

  if (addOnCodeResponse) {
    let tenantId = processInstance.healthSystemTenantId.uuid;
    const url = `/dictionary/codes/description/v2?tenantId=${tenantId}`;
    const effectiveDate = cmxDateTime.format(
      new Date(),
      cmxDateTime.FORMATS.DATE
    );
    const descriptionRequest = {
      ..._cpt,
      code: addOnCodeResponse,
      effectiveDate: effectiveDate
    };

    const descriptions = await api.requestJSON<CMxAPI.CodeResponse>(url, {
      ...REQUEST_INIT_OPTIONS,
      method: HttpMethods.POST,
      body: JSON.stringify([descriptionRequest])
    });
    const normalizeDescriptions = dictionaries.normalizeDescriptions(
      descriptions
    );

    (dispatch as any)(codeDescriptionsAvailable(normalizeDescriptions));
  }

  const updatedProcessInstanceAction = await dispatch(
    updateProcessInstance({ serviceLine, guid: processInstance.guid.uuid }) as any
  );
  return updatedProcessInstanceAction.payload;
});
const timeBasedCodeUpdate = createAsyncThunk<
  AmplifyAPI.ProcessInstance,
  { codeChange: Accelerate.CodeChange; time: string; age: string },
  { rejectValue: CMxCommonApp.CMxError }
>(Bench.TIME_BASED_CODE_UPDATE, async ({ codeChange, time, age }, { dispatch }) => {
  const invoiceURL = getInvoiceURL(codeChange);
  const { processInstance } = codeChange.context;
  const { serviceLine } = getCodeChangeMeta(codeChange);
  const transaction = codeChange?.context?.transaction;
  const addTimeBasedCode = {
    em_code: transaction?.procedure?.procedure_code,
    total_time: time,
    age: age,
    medicare_time_rules: transaction?.procedure?.medicare_time_rules,
    metadata: {
      transaction_id: transaction?.guid
    }
  };
  await api.request(
    `/coding-api/processinstance/${serviceLine}/${processInstance.guid.uuid}/timebasedcode/v1`,
    {
      ...REQUEST_INIT_OPTIONS,
      method: HttpMethods.PUT,
      body: JSON.stringify(addTimeBasedCode)
    }
  );
  const updatedProcessInstanceAction = await dispatch(
    updateProcessInstance({ serviceLine, guid: processInstance.guid.uuid }) as any
  );
  return updatedProcessInstanceAction.payload;
});

const transactionOrderChange = createAsyncThunk<
  AmplifyAPI.ProcessInstance,
  Accelerate.TransactionReorderDetail,
  { rejectValue: CMxCommonApp.CMxError }
>(
  Bench.TRANSACTION_ORDER_CHANGE,
  async (transactionReorderDetail, { dispatch }) => {
    const {
      context: { invoice, processInstance }
    } = transactionReorderDetail;

    await api.request(
      `/coding-api/processinstance/${processInstance.serviceLine}/${processInstance.guid.uuid}/invoice/${invoice.guid}/transactions/v1`,
      {
        ...REQUEST_INIT_OPTIONS,
        method: HttpMethods.PUT,
        body: JSON.stringify(transactionReorderDetail.transactions)
      }
    );
    const invoiceURL = `/coding-api/processinstance/${processInstance.serviceLine}/${processInstance.guid.uuid}/v1?evidence=true&errors=true`;
    const updatedProcessInstance = await api.requestJSON<
      AmplifyAPI.ProcessInstance
    >(invoiceURL, {
      ...REQUEST_INIT_OPTIONS
    });
    dispatch(fetchActivity(updatedProcessInstance) as any);
    return updatedProcessInstance;
  }
);

const removeInvoice = createAsyncThunk<
  AmplifyAPI.ProcessInstance,
  Accelerate.InvoiceDeleteParams,
  { rejectValue: any }
>(Bench.REMOVE_INVOICE, async ({ processInstance, invoiceGuid }, { dispatch }) => {
  const url = `/coding-api/processinstance/${processInstance.serviceLine}/${processInstance.guid.uuid}/invoice/v1?invoiceGuid=${invoiceGuid}`;
  await api.request(url, {
    method: HttpMethods.DELETE
  });

  const updatedProcessInstanceAction = await dispatch(
    updateProcessInstance({
      serviceLine: processInstance.serviceLine,
      guid: processInstance.guid.uuid
    }) as any
  );
  return updatedProcessInstanceAction.payload;
});

const codeSearchDrop = createAsyncThunk<
  AmplifyAPI.ProcessInstance,
  Accelerate.CodeChange,
  { rejectValue: CMxCommonApp.CMxError }
>(Bench.CODE_SEARCH_DROP, async (codeChange, { dispatch }) => {
  const searchDrop = true;
  const transactionURL = getCodeChangeURL(codeChange);
  const invoiceURL = getInvoiceURL(codeChange);

  const {
    context: { invoice }
  } = codeChange;
  let updateDTO: any;

  if (invoice.type === InvoiceTypes.SHARED_CODES) {
    updateDTO = codeChange.codes.map((code, idx) => {
      return {
        diagnosis_code: code.code,
        sequence_number: idx + 1
      };
    });
  } else {
    // run the codeChange through the factory to get the submission
    // then we'll optimistically set the data to that submission
    // if the action fails we'll be rever to the earlier version.
    updateDTO = dtoFactory.updateDTO(codeChange, searchDrop);
    dispatch(
      optimisticCodeChange({
        updateDTO,
        codeChange
      })
    );
  }

  await api.request(transactionURL, {
    ...REQUEST_INIT_OPTIONS,
    method: HttpMethods.PUT,
    body: JSON.stringify(updateDTO)
  });

  return await api.requestJSON<AmplifyAPI.ProcessInstance>(invoiceURL, {
    ...REQUEST_INIT_OPTIONS
  });
});

const codeAdd = createAsyncThunk<
  AmplifyAPI.ProcessInstance,
  Accelerate.CodeAddActionPayload,
  { rejectValue: CMxCommonApp.CMxError }
>(Bench.CODE_ADD, async (codeSearchResult, { dispatch }) => {
  const transactionURL = getCodeChangeURL(codeSearchResult);
  const invoiceURL = getInvoiceURL(codeSearchResult);

  const { context } = codeSearchResult;
  const { invoice, activeItem } = context;

  let addDTO: any;
  if (invoice.type === InvoiceTypes.SHARED_CODES) {
    addDTO = {
      diagnosis_code: codeSearchResult.codeInfo.code,
      code_provenances: codeSearchResult.codeInfo.codeProvenances || null
    };
  } else {
    addDTO = dtoFactory.createDTO(codeSearchResult);
  }

  try {
    /**
     * temporarily update the transaction object (optimistically)
     * so that the selected code is immediately visible in the UI before the server responds
     */
    if (invoice.type !== InvoiceTypes.SHARED_CODES) {
      dispatch(
        updateTransactionOptimistically({
          addDTO: addDTO,
          invoiceGuid: invoice.guid
        })
      );
    }
    const transaction = await api.requestJSON<AmplifyAPI.Transaction>(
      transactionURL,
      {
        ...REQUEST_INIT_OPTIONS,
        method: HttpMethods.POST,
        body: JSON.stringify(addDTO)
      }
    );

    if (codeSearchResult.additionalCodes) {
      for (const codeInfo of codeSearchResult.additionalCodes) {
        await dispatch(
          codesAdd({
            codesInfo: codeInfo,
            context: { ...context, transaction }
          }) as any
        );
      }
    }

    if (invoice.type !== InvoiceTypes.SHARED_CODES) {
      dispatch(
        updateTransaction({
          transaction,
          invoiceGuid: invoice.guid
        })
      );
    }
  } catch (error) {
    (dispatch as any)(
      amplifyFeedback({
        message: `Unable to add ${
          addDTO.hasOwnProperty('modifier')
            ? 'modifier'
            : addDTO.hasOwnProperty('diagnosis')
            ? 'diagnosis'
            : 'procedure'
        } code.`,
        meta: commonEnums.FeedbackMeta.ERROR,
        dismissable: true
      })
    );

    throw error;
  }

  const processInstance = await api.requestJSON<AmplifyAPI.ProcessInstance>(
    invoiceURL,
    {
      ...REQUEST_INIT_OPTIONS
    }
  );

  dispatch(
    fetchRelatedCodes({
      encounterId: activeItem?.encounter_identifier?.number,
      serviceLine: activeItem?.service_line,
      codeType: getCodeTypeShortName(codeSearchResult?.codeInfo?.codeType),
      caseId: activeItem?.case_id
    }) as any
  );
  dispatch(fetchActivity(processInstance) as any);

  return processInstance;
});

const codesAdd = createAsyncThunk<
  AmplifyAPI.ProcessInstance,
  Accelerate.CodesAddCodeBench,
  { rejectValue: CMxCommonApp.CMxError }
>(Bench.BULK_CODE_ADD, async (codeSearchResult, { dispatch }) => {
  const selectResult = {
    context: codeSearchResult.context,
    ...codeSearchResult?.codesInfo?.[0]
  };
  const transactionURL = getCodeChangeURL(selectResult);
  const invoiceURL = getInvoiceURL(selectResult);

  const {
    context: { invoice, activeItem },
    codesInfo
  } = codeSearchResult;

  let response: any;
  try {
    if (invoice.type === InvoiceTypes.SHARED_CODES) {
      for (const codeInfo of codesInfo ?? []) {
        response = await api.requestJSON(transactionURL, {
          ...REQUEST_INIT_OPTIONS,
          method: HttpMethods.POST,
          body: JSON.stringify({
            diagnosis_code: codeInfo.code,
            code_provenances: codeInfo.codeProvenances || null
          })
        });
      }
    } else {
      const addDTO = dtoFactory.BulkcreateDTO(codeSearchResult);
      /**
       * temporarily update the transaction object (optimistically)
       * so that the selected code is immediately visible in the UI before the server responds
       */
      dispatch(
        updateTransactionOptimistically({
          addDTO: addDTO,
          invoiceGuid: invoice.guid
        })
      );
      response = await api.requestJSON(transactionURL, {
        ...REQUEST_INIT_OPTIONS,
        method: HttpMethods.POST,
        body: JSON.stringify(addDTO)
      });
      dispatch(
        updateTransaction({
          transaction: response,
          invoiceGuid: invoice.guid
        })
      );
    }
  } catch (error) {
    (dispatch as any)(
      amplifyFeedback({
        message: `Unable to add codes`,
        meta: commonEnums.FeedbackMeta.ERROR,
        dismissable: true
      })
    );

    throw error;
  }

  const processInstance = await api.requestJSON<AmplifyAPI.ProcessInstance>(
    invoiceURL,
    {
      ...REQUEST_INIT_OPTIONS
    }
  );

  dispatch(
    fetchRelatedCodes({
      encounterId: activeItem?.encounter_identifier?.number,
      serviceLine: activeItem?.service_line,
      codeType: getCodeTypeShortName(codesInfo?.[0]?.codeType),
      caseId: activeItem?.case_id
    }) as any
  );

  dispatch(fetchActivity(processInstance) as any);

  return processInstance;
});

const createTransactionAndAddCode = createAsyncThunk<
  AmplifyAPI.ProcessInstance,
  Accelerate.CodeAddActionPayload,
  { rejectValue: CMxCommonApp.CMxError }
>(Bench.CREATE_TRANSACTION_AND_CODE_ADD, async (codeSearchResult, { dispatch }) => {
  const { codeInfo, context } = codeSearchResult;

  const infoWithContext = { ...codeInfo, context };
  const createTransactionAndCodeURL = getCreateTransactionAndCodeURL(
    infoWithContext
  );

  const {
    context: { invoice, processInstance }
  } = codeSearchResult;

  const createDTO: InvoicesAPI.TransactionCreate = dtoFactory.createTransactionDTO(
    infoWithContext
  );
  /**
   * Optimistically creates a temporary transaction object
   * with the CPT code so that it appears immediately in the UI
   * before the server response is received.
   */

  dispatch(
    createTransactionOptimistically({
      createDTO: createDTO,
      invoiceGuid: invoice.guid
    })
  );
  try {
    const transaction = await api.requestJSON<AmplifyAPI.Transaction>(
      createTransactionAndCodeURL,
      {
        ...REQUEST_INIT_OPTIONS,
        method: HttpMethods.POST,
        body: JSON.stringify(createDTO)
      }
    );
    if (codeSearchResult.additionalCodes) {
      for (const codeInfo of codeSearchResult.additionalCodes) {
        await dispatch(
          codesAdd({
            codesInfo: codeInfo,
            context: { ...context, transaction }
          }) as any
        );
      }
    }
    dispatch(
      replaceOptimisticTransaction({
        transaction: transaction,
        invoiceGuid: invoice.guid
      })
    );
  } catch (error) {
    (dispatch as any)(
      amplifyFeedback({
        message: 'Unable to create transaction and add code.',
        meta: commonEnums.FeedbackMeta.ERROR,
        dismissable: true
      })
    );

    throw error;
  }

  const updatedProcessInstanceAction = await dispatch(
    updateProcessInstance({
      serviceLine: processInstance.serviceLine,
      guid: processInstance.guid.uuid
    }) as any
  );
  return updatedProcessInstanceAction.payload;
});

/**
 * Action for adding new invoice
 */
const addTransaction = createAsyncThunk<
  void,
  Accelerate.NewInvoice,
  { rejectValue: CMxCommonApp.CMxError }
>(Bench.ADD_TRANSACTION, async (newInvoiceData, { dispatch }) => {
  const transactionURL = getTransactionURLForNewInvoice(newInvoiceData);

  // set loading
  dispatch(showAddChargeLoading());

  // request call for add a transaction
  try {
    await api.request(transactionURL, {
      ...REQUEST_INIT_OPTIONS,
      method: HttpMethods.POST
    });
  } catch (e) {
    console.error(e);
    throw e;
  }

  return dispatch(fetchProcessInstance(newInvoiceData.activeItem) as any) as any;
});

const navigatePatient = createAsyncThunk<
  any[],
  CMxAPI.CaseRunV2,
  { rejectValue: CMxCommonApp.CMxError }
>(Launch.NAVIGATE_PATIENT, async (caseRun) => {
  return [];
});

const navigateCoderNotes = createAsyncThunk<
  void,
  CMxCommonApp.CoderNotesParams,
  { rejectValue: CMxCommonApp.CMxError }
>(Launch.NAVIGATE_CODER_COMMENTS, async (coderNotesParams, { dispatch }) => {
  dispatch(
    fetchCoderNotes({
      ...coderNotesParams
    }) as any
  );
});

const navigateProvider = createAsyncThunk<
  XCaliberAPI.ProviderEntityIPC,
  CMxCommonApp.ProviderSearchParams,
  { rejectValue: CMxCommonApp.CMxError }
>(Launch.NAVIGATE_PROVIDER, async (providerInfo, { dispatch, getState }) => {
  const state = getState() as any;
  if (shouldFetchFromDataAccess(state)) {
    const action = await dispatch(navigateProviderDataAccess(providerInfo) as any);
    return action.payload;
  } else {
    const action = await dispatch(navigateProviderXCaliber(providerInfo) as any);
    return action.payload;
  }
});


export const navigateProviderXCaliber = createAsyncThunk<
  XCaliberAPI.ProviderEntityIPC,
  CMxCommonApp.ProviderSearchParams,
  { rejectValue: CMxCommonApp.CMxError }
>(Launch.NAVIGATE_PROVIDER_XCALIBER, async (providerInfo) => {
  if (!providerInfo?.providerNumber) return null;

  const xcaliberToken = fetchXcaliberToken();
  const queryURL = `${CONSTELLATION_SERVICES_LOCATION}/${xcaliberToken.tenantPath}/directory/api/v1/entities/provider/entries/functions/search_billing_providers/execute`;

  let body: Record<string, string> = { provider_name: providerInfo.providerName ?? "" };

  if (providerInfo.tenantCode) {
    body.location_name = providerInfo.tenantCode;
  }

  const providerData = await api.requestJSON<CMxAPI.PageableList<XCaliberAPI.ProviderEntityIPC>>(
    queryURL,
    {
      ...REQUEST_INIT_OPTIONS,
      headers: { Authorization: `Bearer ${xcaliberToken.token}` },
      method: HttpMethods.POST,
      body: JSON.stringify(body),
    }
  );
  return providerData.content?.find(
    (provider) => provider?.provider_id === providerInfo.providerNumber
  ) ?? null;
});

export const navigateProviderDataAccess = createAsyncThunk<
  XCaliberAPI.ProviderEntityIPC,
  CMxCommonApp.ProviderSearchParams,
  { rejectValue: CMxCommonApp.CMxError }
>(Launch.NAVIGATE_PROVIDER_DATAACCESS, async (providerInfo, { getState }) => {
  if (!providerInfo?.providerNumber) return null;

  const state = getState() as any;
  const queryURL = '/dataaccess/read/v1';

  const providerSearchBody: CMxCommonApp.ProviderSearchRequest = {
  ...buildDataAccessRequestObject(
    state,
    "searchForProvidersByName",
    "searchbillingprovider"
  ),
  filterFieldsMap: {
    provider_name: providerInfo.providerName ?? "",
    ...(providerInfo.tenantCode
      ? { location_name: providerInfo.tenantCode }
      : {}),
  },
};


  const providerData = await api.requestJSON<CMxAPI.PageableList<XCaliberAPI.ProviderEntityIPC>>(
    queryURL,
    {
      method: HttpMethods.POST,
      body: JSON.stringify(providerSearchBody),
    }
  );

  return providerData.content?.find(
    (provider) => provider?.provider_id === providerInfo.providerNumber
  ) ?? null;
});



const navigateDetail = createAsyncThunk<void, string, { rejectValue: CMxCommonApp.CMxError }>(
  Launch.NAVIGATE_DETAIL,
  async (caseNavigate, { dispatch }) => {
    const [caseId, processInstanceGuid] = caseNavigate.split(':');
    const { path: slPath, caseDetailVersion } = appServiceLines.get();

    const url = `${slPath}/caseinstance/${caseId}/details/${caseDetailVersion}?evidence=true&processTypes=PREDICTION&processTypes=ADDENDUM`;

    const withDetails = await api.requestJSON<CMxAPI.Case>(url);
    //
    let activeItem = withDetails.caseRuns.find(
      caseRun => caseRun.process_instance_guid === processInstanceGuid
    );
    if (!activeItem) {
      activeItem = withDetails.caseRuns[0];
    }

    dispatch(fetchProcessInstance(activeItem) as any);
    const { loadCase } = await import('./cases.action');
    await loadCase(activeItem, dispatch);
  }
);

type ToggleInfo = {
  APImethod: string;
  techCodableTransaction: CMxCommonApp.TechCodableTransaction;
};

/**
 * Function for toggle tech pro codes
 * @param method
 * @param techCodableTransaction
 * @param dispatch
 * @returns
 */
const toggleTechPro = createAsyncThunk<any, ToggleInfo, { rejectValue: CMxCommonApp.CMxError }>(
  Bench.TOGGLE_TECH_PRO,
  async (toggleInfo, { dispatch }) => {
    // start loading process and optimistic change
    dispatch(
      setLoadingTechPro({
        guid: toggleInfo.techCodableTransaction.invoice.guid,
        value: true
      })
    );

    dispatch(optimisticProTechChange(toggleInfo.techCodableTransaction));

    const bodyObject: CMxCommonApp.TechCodableTransaction = {
      transactionGuid: toggleInfo.techCodableTransaction.transactionGuid
    };

    if (toggleInfo.techCodableTransaction.hasOwnProperty('technicalCode')) {
      bodyObject.technicalCode =
        toggleInfo.techCodableTransaction.technicalCode;
    } else if (
      toggleInfo.techCodableTransaction.hasOwnProperty('mappedProcedure')
    ) {
      bodyObject.mappedProcedure =
        toggleInfo.techCodableTransaction.mappedProcedure;
    }

    const serviceLineConfig = appServiceLines.determineServiceLineConfig({
      name: toggleInfo.techCodableTransaction.processInstance.serviceLine
    });

    try {
      // request to toggle pro/tech code
      await api.request(
        `/coding-api/processinstance/${serviceLineConfig.amplifyPath}/${toggleInfo.techCodableTransaction.processInstance.guid.uuid}/codes/v1`,
        {
          ...REQUEST_INIT_OPTIONS,
          method: toggleInfo.APImethod,
          body: JSON.stringify(bodyObject)
        }
      );
    } catch (error) {
      (dispatch as any)(
        amplifyFeedback({
          message: 'There were issues updating pro/tech charge.',
          meta: commonEnums.FeedbackMeta.ERROR,
          dismissable: true
        })
      );
    } finally {
      const processInstance = await getProcessInstance(
        `/coding-api/processinstance/${serviceLineConfig.amplifyPath}/${toggleInfo.techCodableTransaction.processInstance.guid.uuid}/v1?evidence=true&errors=true`
      );

      dispatch(
        setLoadingTechPro({
          guid: toggleInfo.techCodableTransaction.invoice.guid,
          value: false
        })
      );

      return processInstance;
    }
  }
);

/**
 * Async function for enable tech/pro charges
 */
const enableTechPro = createAsyncThunk<
  AmplifyAPI.ProcessInstance,
  CMxCommonApp.TechCodableTransaction,
  { rejectValue: CMxCommonApp.CMxError }
>(
  Bench.ENABLE_TECH_PRO,
  async (
    techCodableTransaction: CMxCommonApp.TechCodableTransaction,
    { dispatch }
  ) => {
    const action = await dispatch(
      toggleTechPro({ APImethod: HttpMethods.POST, techCodableTransaction }) as any
    );
    return action.payload;
  }
);

/**
 * Async function for disable tech/pro charges
 */
const disableTechPro = createAsyncThunk<
  AmplifyAPI.ProcessInstance,
  CMxCommonApp.TechCodableTransaction,
  { rejectValue: CMxCommonApp.CMxError }
>(
  Bench.DISABLE_TECH_PRO,
  async (
    techCodableTransaction: CMxCommonApp.TechCodableTransaction,
    { dispatch }
  ) => {
    const action = await dispatch(
      toggleTechPro({ APImethod: HttpMethods.DELETE, techCodableTransaction }) as any
    );
    return action.payload;
  }
);

/**
 * Function for toggle tech pro codes
 * @param method
 * @param {ToggleChargeableCaseType} toggleChargeableCaseObject
 * @param dispatch
 * @returns
 */
const toggleChargeableCase = createAsyncThunk<
  AmplifyAPI.ProcessInstance,
  Accelerate.ToggleChargeableCaseType,
  { rejectValue: CMxCommonApp.CMxError }
>(
  Bench.TOGGLE_CHARGEABLE_CASE,
  async (toggleChargeableCaseObject, { dispatch }) => {
    try {
      // request to toggle pro/tech code
      await api.request(
        `/coding-api/processinstance/${toggleChargeableCaseObject.serviceLine}/${toggleChargeableCaseObject.processInstanceGuid}/chargeable/v1?isChargeable=${toggleChargeableCaseObject?.isChargeable}`,
        {
          ...REQUEST_INIT_OPTIONS,
          method: HttpMethods.PUT
        }
      );
    } catch (error) {
      (dispatch as any)(
        amplifyFeedback({
          message: 'There was an issue updating chargeble state.',
          meta: commonEnums.FeedbackMeta.ERROR,
          dismissable: true
        })
      );
    } finally {
      dispatch(hideExclusionCaseModal());
      const processInstance = await getProcessInstance(
        `/coding-api/processinstance/${toggleChargeableCaseObject.serviceLine}/${toggleChargeableCaseObject.processInstanceGuid}/v1?evidence=true&errors=true`
      );

      dispatch(fetchActivity(processInstance) as any);
      return processInstance;
    }
  }
);

/**
 * Function for clearing late case update notification and taking the case off the worklist view
 * Future changes may be needed to support an undo option also
 * @param {AmplifyAPI.ProcessInstance} processInstance
 * @param dispatch
 * @returns
 */
const clearLateCaseNotification = createAsyncThunk<
  void,
  AmplifyAPI.ProcessInstance,
  { rejectValue: CMxCommonApp.CMxError }
>(Bench.CLEAR_LATE_CASE_UPDATE, async (processInstance, { dispatch }) => {
  try {
    api.request(
      `/coding-api/processinstance/${processInstance?.serviceLine}/${processInstance?.guid?.uuid}/latecase/update/v1?dismiss=true`,
      {
        ...REQUEST_INIT_OPTIONS,
        method: HttpMethods.PUT
      }
    );
  } catch (error) {
    (dispatch as any)(
      amplifyFeedback({
        message: 'There was an issue clearing the notification.',
        meta: commonEnums.FeedbackMeta.ERROR,
        dismissable: true
      })
    );
  } finally {
    dispatch(toggleLateCaseClearModal(false));
    dispatch(fetchActivity(processInstance) as any);
  }
});

/**
 * Function for holding a case
 * @param method
 * @param {any} holdCaseObject
 * @param dispatch
 * @returns
 */
const holdCase = createAsyncThunk<void, CMxAPI.HoldCaseType, { rejectValue: CMxCommonApp.CMxError }>(
  Bench.HOLD_CASE,
  async (holdCaseObject, { dispatch }) => {
    let response: any;
    const processInstance = holdCaseObject.processInstance;
    try {
      const holdDTO = {
        hold_reason: holdCaseObject.holdReason,
        comment: holdCaseObject.comment,
        coding_manager_id: holdCaseObject?.coding_manager_id ?? null
      };

      response = await api.request(
        `/coding-api/processinstance/${processInstance.serviceLine}/${processInstance.guid.uuid}/hold/v1`,
        {
          ...REQUEST_INIT_OPTIONS,
          method: HttpMethods.PUT,
          body: JSON.stringify(holdDTO)
        }
      );
    } catch (error) {
      (dispatch as any)(
        amplifyFeedback({
          message: 'There was an issue upon hold.',
          meta: commonEnums.FeedbackMeta.ERROR,
          dismissable: true
        })
      );
    } finally {
      dispatch(getHoldCaseForProcessInstance(processInstance) as any);
      dispatch(hideHoldCaseModal());
      dispatch(hideCaseCommentModal());
      dispatch(fetchActivity(processInstance) as any);
    }
  }
);

/**
 * Function for fetching hold case
 * @param method
 * @param {any} holdCaseObject
 * @param dispatch
 * @returns
 */
const getHoldCaseForProcessInstance = createAsyncThunk<
  CMxAPI.HoldCaseResponse,
  AmplifyAPI.ProcessInstance,
  { rejectValue: CMxCommonApp.CMxError }
>(Bench.FETCH_HOLD_CASE, async (processInstance) => {
  const response = await api.requestJSON<CMxAPI.HoldCaseResponse>(
    `/coding-api/processinstance/${processInstance.serviceLine}/${processInstance.guid.uuid}/hold/v1`,
    {
      ...REQUEST_INIT_OPTIONS,
      method: HttpMethods.GET
    }
  );

  return response;
});

/**
 * Function for getting case versions
 * @param method
 * @param {any} ProcessInstance
 * @param dispatch
 * @returns {AmplifyAPI.CaseVersion[]}
 */
const getCaseVersions = createAsyncThunk<
  AmplifyAPI.CaseVersion[],
  AmplifyAPI.ProcessInstance,
  { rejectValue: CMxCommonApp.CMxError }
>(Bench.FETCH_CASE_VERSIONS, async (processInstance) => {
  return await api.requestJSON<Array<AmplifyAPI.CaseVersion>>(
    `/coding-api/processinstance/${processInstance.serviceLine}/${processInstance.guid.uuid}/caseversion/v1`,
    {
      ...REQUEST_INIT_OPTIONS,
      method: HttpMethods.GET
    }
  );
});


const loadNextOrPreviousCase = createAction<any>(
  Bench.LOAD_NEXT_OR_PREVIOUS_CASE
);

const loadWorkitemInAmplify = createAction<CMxCommonApp.LaunchWorkItemConfig>(
  Bench.LOAD_WORKITEM_IN_AMPLIFY
);

const loadCursorAPIContext = createAction<CMxCommonApp.CursorAPIContext>(
  Bench.LOAD_CURSOR_API_CONTEXT
);

const resetCursorAPIContext = createAction(Bench.RESET_CURSOR_API_CONTEXT);

const CONSTELLATION_SERVICES_LOCATION = '/constellation';

// feature flags
/**
 * Function for fetching case-data-service and get entries
 * @param method
 * @param {any} holdCaseObject
 * @param dispatch
 * @returns
 */
const getCaseEntriesWithFilter = createAsyncThunk<
  CMxAPI.CaseEntriesResponse,
  CMxAPI.CaseEntriesRequest,
  { rejectValue: CMxCommonApp.CMxError }
>(
  Bench.FETCH_CASE_INTERACTIONS_INFINITE,
  async (
    {
      caseRun,
      limit,
      page,
      timelineInfiniteLoad,
      useDataAccessAmplify,
      messageTypeFilter,
      dateFilter,
      endDateFilter,
      ui
    }
  ) => {
    return conditionalUtils.capabilityValue(useDataAccessAmplify)
      ? getDataAccessPatientTimelinewithFilter({
          caseRun,
          limit,
          page,
          timelineInfiniteLoad,
          messageTypeFilter,
          dateFilter,
          endDateFilter,
          ui
        })
      : getPatientTimelineWithFilter({
          caseRun,
          limit,
          page,
          timelineInfiniteLoad,
          messageTypeFilter,
          dateFilter,
          endDateFilter
        });
  }
);

/**
 * Function for fetching case-data-service and get entries
 * @param method
 * @param {any} holdCaseObject
 * @param dispatch
 * @returns
 */
const getCaseEntries = createAsyncThunk<
  CMxAPI.CaseEntriesResponse,
  CMxAPI.CaseEntriesRequest,
  { rejectValue: CMxCommonApp.CMxError }
>(
  Bench.FETCH_CASE_INTERACTIONS,
  async (
    { caseRun, limit, page, timelineInfiniteLoad, useDataAccessAmplify, ui }
  ) => {
    return conditionalUtils.capabilityValue(useDataAccessAmplify)
      ? getDataAccessPatientTimelineCaseWithoutFilter({
          caseRun,
          limit,
          page,
          timelineInfiniteLoad,
          ui
        })
      : getPatientTimelineWithoutFilter({
          caseRun,
          limit,
          page,
          timelineInfiniteLoad
        });
  }
);

/**
 * Function for fetching patient message count
 * @param method
 * @param {any} holdCaseObject
 * @param dispatch
 * @returns
 */
const getPatientMessageCounts = createAsyncThunk<
  CMxAPI.MessageCounts[],
  CMxCommonApp.TimelineCountParams,
  { rejectValue: CMxCommonApp.CMxError }
>(Bench.FETCH_MESSAGE_COUNTS, async (timelineCountParams) => {
  return conditionalUtils.capabilityValue(
    timelineCountParams?.useDataAccessAmplify
  )
    ? getDataAccessPatientMessageCountWithoutFilter(timelineCountParams)
    : getPatientMessageCountWithoutFilter(timelineCountParams);
});

/**
 * Function for fetching patient message count
 * @param method
 * @param {any} holdCaseObject
 * @param dispatch
 * @returns
 */
const getPatientMessageCountsWithFilter = createAsyncThunk<
  CMxAPI.MessageCounts[],
  CMxCommonApp.TimelineCountParams,
  { rejectValue: CMxCommonApp.CMxError }
>(
  Bench.FETCH_MESSAGE_COUNTS_INFINITE,
  async (timelineCountParams) => {
    return conditionalUtils.capabilityValue(
      timelineCountParams?.useDataAccessAmplify
    )
      ? getDataAccessPatientMessageCountwithFilter(timelineCountParams)
      : getPatientMessageCountWithFilter(timelineCountParams);
  }
);

type FlagItemApiRow = Pick<
  CMxAPI.FlagItem,
  'flag_type' | 'flag' | 'alternate_visit_id'
>;

const FLAG_ITEM_SOURCE_ORDER: CMxAPI.FlagItemSource[] = [
  'encounter',
  'patient'
];

const normalizeFlagText = (flag: string) => flag.trim();

/** Dedupe by flag identity; alternate_visit_id can differ on patient-level rows. */
const flagItemMergeKey = (item: FlagItemApiRow) =>
  `${item.flag_type}|${normalizeFlagText(item.flag)}`;

const tagFlagItemsWithSource = (
  items: FlagItemApiRow[],
  source: CMxAPI.FlagItemSource
): CMxAPI.FlagItem[] => items.map(item => ({ ...item, flag_src: [source] }));

export const mergeFlagItems = (items: CMxAPI.FlagItem[]): CMxAPI.FlagItem[] => {
  const byKey = new Map<string, CMxAPI.FlagItem>();

  for (const item of items) {
    const key = flagItemMergeKey(item);
    const existing = byKey.get(key);

    if (!existing) {
      byKey.set(key, {
        ...item,
        flag: normalizeFlagText(item.flag),
        flag_src: [...(item.flag_src ?? [])]
      });
      continue;
    }

    const combined = new Set([
      ...(existing.flag_src ?? []),
      ...(item.flag_src ?? [])
    ]);
    byKey.set(key, {
      ...existing,
      flag_src: FLAG_ITEM_SOURCE_ORDER.filter(source => combined.has(source))
    });
  }

  return Array.from(byKey.values());
};

const fetchFlagItemsXCaliber = async (
  caseRun: CMxAPI.CaseRunV2,
  body: { alternate_visit_id?: string; mrn?: string }
): Promise<FlagItemApiRow[]> => {
  const xcaliberToken = fetchXcaliberToken();
  const status = xcaliberToken?.status?.toUpperCase() ?? '';

  if (status !== 'SUCCESS') {
    return [];
  }

  const queryURL = `${CONSTELLATION_SERVICES_LOCATION}/${xcaliberToken.tenantPath}/directory/api/v1/entities/patient/entries/functions/get_encounter_info/execute`;
  const response = await api.requestJSON<
    CMxAPI.PageableList<FlagItemApiRow>
  >(queryURL, {
    ...REQUEST_INIT_OPTIONS,
    headers: { Authorization: `Bearer ${xcaliberToken.token}` },
    method: HttpMethods.POST,
    body: JSON.stringify(body)
  });

  return response.content ?? [];
};

const fetchFlagItemsDataAccess = async (
  caseRun: CMxAPI.CaseRunV2,
  state: any,
  filterFieldsMap: CMxCommonApp.EncounterInfoRequest['filterFieldsMap']
): Promise<FlagItemApiRow[]> => {
  const queryURL = '/dataaccess/read/v1';

  const body: CMxCommonApp.EncounterInfoRequest = {
    ...buildDataAccessRequestObject(state, 'getEncounterInfo', 'encounterinfo'),
    filterFieldsMap
  };

  const response = await api.requestJSON<
    CMxAPI.PageableList<FlagItemApiRow>
  >(queryURL, {
    method: HttpMethods.POST,
    body: JSON.stringify(body)
  });

  return response.content ?? [];
};

const fetchAndMergeFlagItems = async (
  caseRun: CMxAPI.CaseRunV2,
  state: any,
  useDataAccess: boolean
): Promise<CMxAPI.FlagItem[]> => {
  const [encounterRows, patientRows] = await Promise.all([
    useDataAccess
      ? fetchFlagItemsDataAccess(caseRun, state, {
          alternate_visit_id: caseRun?.encounter_identifier?.number ?? ''
        })
      : fetchFlagItemsXCaliber(caseRun, {
          alternate_visit_id: caseRun?.encounter_identifier?.number
        }),
    useDataAccess
      ? fetchFlagItemsDataAccess(caseRun, state, {
          patient_mrn: caseRun?.patient_identifier?.number ?? ''
        })
      : fetchFlagItemsXCaliber(caseRun, {
          mrn: caseRun?.patient_identifier?.number
        })
  ]);

  return mergeFlagItems([
    ...tagFlagItemsWithSource(encounterRows, 'encounter'),
    ...tagFlagItemsWithSource(patientRows, 'patient')
  ]);
};

/**
 * Fetches flag items for a given case run (encounter + patient MRN), merges
 * results, and tags each row with `flag_src` provenance.
 *
 * - **Data Access Flow** → two `/dataaccess/read/v1` calls when
 *   `shouldFetchFromDataAccess` is true.
 * - **XCaliber Flow** → two `get_encounter_info/execute` calls otherwise.
 */
const getFlagItems = createAsyncThunk<
  CMxAPI.FlagItem[],
  CMxAPI.CaseRunV2,
  { rejectValue: CMxCommonApp.CMxError }
>(Bench.FETCH_FLAGS, async (caseRun, { getState }) => {
  const state = getState() as any;
  return fetchAndMergeFlagItems(
    caseRun,
    state,
    shouldFetchFromDataAccess(state)
  );
});

export const getFlagItemsXCaliber = createAsyncThunk<
  CMxAPI.FlagItem[],
  CMxAPI.CaseRunV2,
  { rejectValue: CMxCommonApp.CMxError }
>(Bench.FETCH_FLAGS_XCALIBER, async caseRun => {
  const rows = await fetchFlagItemsXCaliber(caseRun, {
    alternate_visit_id: caseRun?.encounter_identifier?.number
  });
  return tagFlagItemsWithSource(rows, 'encounter');
});

export const getFlagItemsDataAccess = createAsyncThunk<
  CMxAPI.FlagItem[],
  CMxAPI.CaseRunV2,
  { rejectValue: CMxCommonApp.CMxError }
>(Bench.FETCH_FLAGS_DATAACCESS, async (caseRun, { getState }) => {
  const state = getState() as any;
  const rows = await fetchFlagItemsDataAccess(caseRun, state, {
    alternate_visit_id: caseRun?.encounter_identifier?.number ?? ''
  });
  return tagFlagItemsWithSource(rows, 'encounter');
});

const getCasesWithEncounter = createAsyncThunk<
  CMxAPI.PageableList<CMxAPI.RelatedCase>,
  CMxCommonApp.HARListPayload,
  { rejectValue: CMxCommonApp.CMxError }
>(Bench.FETCH_CASES_WITH_ENCOUNTER, async (params, { dispatch }) => {
  try {
    const {
      encounterId,
      sortablePageable,
      serviceLine,
      filterSelection
    } = params;

    const searchParams = searchParamsFactory(sortablePageable);
    searchParams.set('encounterId', encounterId);

    const filterClauses = getRelatedCasesFilterRequest(
      encounterId,
      filterSelection
    );

    const result = await api.requestJSON<
      CMxAPI.PageableList<CMxAPI.RelatedCase>
    >(`/coding-api/${serviceLine}/relatedcases/v2?${searchParams.toString()}`, {
      method: HttpMethods.POST,
      body: JSON.stringify(filterClauses)
    });
    return result;
  } catch (error) {
    (dispatch as any)(
      amplifyFeedback({
        message: 'There was an error fetching related cases.',
        meta: commonEnums.FeedbackMeta.ERROR,
        dismissable: true
      })
    );
  }
});

const getCodesWithEncounter = createAsyncThunk<
  CMxAPI.SuggestedCodeResponse,
  CMxCommonApp.EncounterCodesParams,
  { rejectValue: CMxCommonApp.CMxError }
>(Bench.FETCH_CODES_WITH_ENCOUNTER, async (params, { dispatch }) => {
  try {
    const { encounterId, codeType, serviceLine, caseId } = params;

    const path = `/coding-api/${serviceLine}/relatedcases/codes/v1`;
    const result = await api.requestJSON<CMxAPI.SuggestedCodeResponse>(path, {
      method: HttpMethods.POST,
      body: JSON.stringify({
        encounter_id: encounterId,
        code_type: codeType,
        case_id: caseId
      })
    });
    return result;
  } catch (error) {
    (dispatch as any)(
      amplifyFeedback({
        message: 'There was an error fetching suggested codes.',
        meta: commonEnums.FeedbackMeta.ERROR,
        dismissable: true
      })
    );
  }
});


const getRelatedCodes = createAsyncThunk<
  CMxCommonApp.CodeInfo[],
  CMxCommonApp.RelatedCodesParams,
  { rejectValue: CMxCommonApp.CMxError }
>(Bench.GET_RELATED_CODES, async (params, { dispatch, rejectWithValue }) => {
  try {
    const { codeType, serviceLine, caseId } = params;

    const path = `/api/case-data/${serviceLine}/caseinstance/relatedcodes/v1`;

    const result = await api.requestJSON<CMxCommonApp.CodeInfo[]>(
      path,
      {
        method: HttpMethods.POST,
        body: JSON.stringify({
          case_id: String(caseId),
          code_type: codeType.toLowerCase()
        })
      }
    );
    return result;
  } catch (error) {
    (dispatch as any)(
      amplifyFeedback({
        message: 'There was an error fetching related codes.',
        meta: commonEnums.FeedbackMeta.ERROR,
        dismissable: true
      })
    );
    return rejectWithValue(error as CMxCommonApp.CMxError);
  }
});


const fetchRelatedCodes = createAsyncThunk<
  any,
  CMxCommonApp.RelatedCodesParams,
  { rejectValue: CMxCommonApp.CMxError }
>(Bench.FETCH_RELATED_CODES, async (params, { dispatch, getState }) => {
  const state = (getState as any)() as Accelerate.BenchApplicationState;

  const isTransactionV2 = conditionalUtils.capabilityValue(
    state.capabilities?.capabilities?.transactionV2?.on
  );

  const isRelatedCodesEnabled = isResourceAllowed(
    state.capabilities?.userContext as CMxAPI.OrganizationContext,
    commonEnums.ResourceId.ENABLE_RELATED_CASE_CODES_UI,
    params.serviceLine
  );

  if (!isTransactionV2 || !isRelatedCodesEnabled) {
    return;
  }

  const resultAction = await dispatch(getRelatedCodes(params));
  return (resultAction as any).payload;
});

const getCCICustomClaimCrossCases = createAsyncThunk<
  CMxAPI.CaseRun[],
  { activeItem: CMxAPI.CaseRun; processInstance: AmplifyAPI.ProcessInstance },
  { rejectValue: CMxCommonApp.CMxError }
>(
  Bench.FETCH_CCI_CUSTOM_CLAIM_CROSS_CASES,
  async ({ activeItem, processInstance }, { dispatch }) => {
    const hasCCIEditCustomClaimErrors = processInstance.errors.some(
      error =>
        error.message_key === IssueTypeErrors.CCI_EDIT ||
        error.message_key === IssueTypeErrors.CUSTOM_RULES
    );

    if (!hasCCIEditCustomClaimErrors) {
      return [];
    }

    try {
      if (
        !activeItem ||
        !processInstance?.guid?.uuid ||
        !activeItem.service_line
      ) {
        console.error(
          'Invalid activeItem, please try again or contact support. ',
          activeItem
        );
        throw new Error('Invalid activeItem');
      }

      const result = await api.requestJSON<
        CMxAPI.ClaimsEditRelatedCasesResponse
      >(
        `/api/cases/${activeItem.service_line.toUpperCase()}/caseinstance/${
          processInstance?.guid?.uuid
        }/claimseditgroup/v1`,
        {
          method: HttpMethods.POST,
          body: JSON.stringify({
            date_of_service: activeItem.date_of_service,
            billing_provider_number: activeItem.billing_provider_number.number
          })
        }
      );

      return result.caseRuns;
    } catch (error) {
      (dispatch as any)(
        amplifyFeedback({
          message: 'There was an error fetching custom claims related cases',
          meta: commonEnums.FeedbackMeta.ERROR,
          dismissable: true
        })
      );
    }
  }
);

const openWindow = createAction<CMxCommonApp.WindowOpenArguments>(
  UserInterface.OPEN_CHILD_WINDOW
);
const getDocuments = async (
  caseRun: CMxAPI.CaseRun,
  tenantInfo: CMxAPI.Organization,
  automateGen?: string
) => {
  let filterParams: {
    tenantId: string;
    businessKey?: string;
    fillerOrderNumber?: string;
  }[];

  const isIPCServiceLine = appServiceLines.isIPC(caseRun.service_line);

  if (automateGen === AutomateGeneration.GEN3) {
    filterParams = buildBusinessKeyFilters(caseRun, tenantInfo);
  } else if (isIPCServiceLine) {
    const docsSeen = new Set<string>();
    filterParams = (caseRun.notes as CMxAPI.Note[])
      ?.filter(note => note.unique_document_number)
      .map(note => {
        const docId = note.unique_document_number as string;
        docsSeen.add(docId);
        return {
          tenantId: `${tenantInfo.tenantId}`,
          uniqueDocumentNumber: docId
        };
      });
  } else {
    filterParams = [
      {
        tenantId: `${tenantInfo.tenantId}`,
        fillerOrderNumber: caseRun.order_filler_number.number
      }
    ];
  }

  // docs metadata filter
  const response: CMxAPI.DocumentMetadataWithNotices[] = [];
  let docs = await fetchDocumentsByFilterParams(filterParams);

  if (docs.length === 0 && isIPCServiceLine) {
    // if no docs found, try again with businessKey
    filterParams = buildBusinessKeyFilters(caseRun, tenantInfo);
    docs = await fetchDocumentsByFilterParams(filterParams);
  }

  if (automateGen === AutomateGeneration.GEN3 || isIPCServiceLine) {
    const { MimeTypes } = documentUtils;
    const docGroupProperty = isIPCServiceLine
      ? 'uniqueDocumentNumber'
      : 'externalId';
    const groupedDocs: Dictionary<CMxAPI.DocumentMetadata[]> = objectUtils.groupBy(
      docs,
      docGroupProperty
    );

    Object.entries(groupedDocs).forEach(([externalId, docs]) => {
      // group documents by mimeType
      const groupByDocs: Dictionary<CMxAPI.DocumentMetadata[]> = objectUtils.groupBy(
        docs,
        'mimeType'
      );
      const precedence = [MimeTypes.HTML, MimeTypes.PDF, MimeTypes.TEXT];
      const doc = documentUtils.firstAvailable(groupByDocs, precedence);
      if (doc) {
        // check if conversion is expected
        // add to response if conversion is expected and missing
        const conversionTargets = documentUtils.getExpectedConversionTargets(
          groupByDocs
        );
        const foundConversion = conversionTargets.includes(doc.mimeType);

        response.push({
          ...doc,
          missedConversion: conversionTargets.length > 0 && !foundConversion
        });
      }
    });
  } else {
    response.push(...docs);
  }

  return response;
};

/**
 * Builds an array of filter parameters for fetching documents by business key.
 *
 * @param caseRun - The case run object containing the case run UID and child case list.
 * @param tenantInfo - The organization information containing the tenant ID.
 * @returns An array of filter parameters with tenant ID and business key.
 */
const buildBusinessKeyFilters = (
  caseRun: CMxAPI.CaseRun,
  tenantInfo: CMxAPI.Organization
) => {
  const filterParams = [
    {
      tenantId: `${tenantInfo.tenantId}`,
      businessKey: caseRun.caserun_uid ?? ''
    }
  ];
  caseRun.child_case_list?.forEach(childCase => {
    filterParams.push({
      tenantId: `${tenantInfo.tenantId}`,
      businessKey: childCase.caserun_uid ?? ''
    });
  });
  return filterParams;
};

/**
 * Fetches a list of documents based on the provided filter parameters.
 *
 * @param filterParams - An array of filter parameters, each containing a tenant ID and an optional business key or filler order number.
 * @returns A promise that resolves to an array of `CMxAPI.DocumentMetadata` objects representing the fetched documents.
 */
const fetchDocumentsByFilterParams = async (
  filterParams: {
    tenantId: string;
    businessKey?: string;
    fillerOrderNumber?: string;
  }[]
) => {
  const documentRequests = filterParams.map(filterParam => {
    //@ts-ignore
    const docFilter = new URLSearchParams(filterParam);
    return api.requestJSON<CMxAPI.DocumentMetadata[]>(
      `/documents/v2?${docFilter}`
    );
  });

  // get list of documents
  const allPromises = await Promise.all<CMxAPI.DocumentMetadata[]>(
    documentRequests
  );
  const docs = allPromises.flatMap(doc => doc);
  return docs;
};

const getDocumentsAsHtml = createAsyncThunk<
  CMxCommonApp.DecoratedNote[],
  CMxAPI.CaseRun,
  { rejectValue: CMxCommonApp.SubmitError }
>('GET_HTML', async (caseRun, { dispatch }) => {
  const { tenant_name, child_case_list = [] } = caseRun;
  const tenantInfo = await api.requestJSON<CMxAPI.Organization>(
    `/organization/by-name/${tenant_name}/v1`
  );

  let docs = await getDocuments(caseRun, tenantInfo);

  let htmlNotes: CMxCommonApp.DecoratedNote[] = [];
  let caseRuns: CMxAPI.CaseRun[] = [caseRun];

  // Get any additional caseRuns so we have document name data
  for (const child of child_case_list) {
    if (child.case_uid !== caseRun.case_uid) {
      const serviceLineConfig = appServiceLines.determineServiceLineConfig({
        name: caseRun.service_line
      });

      const caseParams = new URLSearchParams(serviceLineConfig.caseDetailParms);

      const expandedCaseDetail: CMxAPI.Case = await api.requestJSON<
        CMxAPI.Case
      >(
        `${serviceLineConfig.path}/caseinstance/${child.case_uid}/details/${
          serviceLineConfig.caseDetailVersion
        }?${caseParams.toString()}`,
        {
          ...REQUEST_INIT_OPTIONS
        }
      );

      caseRuns = caseRuns.concat(expandedCaseDetail?.caseRuns ?? []);
    }
  }

  let newDocs = [];
  const allowedDocs = new Set(['application/pdf', 'text/html', 'text/plain']);
  for (let doc of docs) {
    const { id, mimeType } = doc;

    if (!id) {
      console.warn(`no id for document`, doc);
      continue;
    }

    if (allowedDocs.has(mimeType)) {
      // caseruns are an amalgam of all case runs associated with the case
      // so we need to find the caseRun that should be looked up in order
      // with this document
      caseRuns.sort((a, b) => {
        // if either of the docs a parent, but not both, thenn the one with the parent should be first
        if (!a.parent_case || !b.parent_case) {
          if (a.parent_case) {
            return 1;
          }
          if (b.parent_case) {
            return -1;
          }
        }
        if (
          a.case_created_datetime === null ||
          b.case_created_datetime === null
        )
          return 0;
        // fallback to the lastest created date
        return a.case_created_datetime > b.case_created_datetime ? 1 : -1;
      });

      const caseRun = caseRuns?.find(caseRun => {
        return caseRun.notes?.find(note => {
          //@ts-ignore
          if (note.unique_document_number === doc.uniqueDocumentNumber)
            return true;
          return caseRun.caserun_uid === doc.businessKey;
        });
      });
      const notes = caseRun?.notes as CMxAPI.Note[];
      const caseRunNote = notes?.find(note => {
        return note.unique_document_number === doc.uniqueDocumentNumber;
      });

      try {
        const endpoint = `/documents/download/${id}${
          mimeType === 'application/pdf' ? '/encoded' : ''
        }/v1`;
        const content = await api.request(endpoint);
        const text = await content.text();
        const htmlNote = documentUtils.decoratedNoteFactory({
          description: caseRunNote?.description ?? doc.documentDescription,
          content: text,
          filename: caseRunNote?.note_specialty_designation ?? 'Document',
          type: doc.documentType,
          document_id: doc.documentId,
          missedConversion: doc.missedConversion ?? false,
          id,
          filetype: mimeType,
          datetime: caseRun?.datetime_of_service ?? '',
          primary: caseRun?.primary_case_indicator ?? false,
          unique_document_number: caseRunNote?.unique_document_number
        });

        htmlNotes.push(htmlNote);
        newDocs.push({
          ...doc,
          documentName: caseRunNote?.note_specialty_designation ?? 'Document',
          primary: caseRun.primary_case_indicator
        });
      } catch (e) {
        console.error('CB error fetching document', doc, e);
      }
    }
  }

  docs = newDocs.filter(doc => doc.documentId !== null);

  dispatch(docMetadataAvailable(docs));
  return htmlNotes;
});

const loadRoles = createAction<string[]>(Bench.LOAD_ROLES);

const fetchNoteTypes = createAsyncThunk<
  Accelerate.DictionaryRowData<CMxCommonApp.NoteTypeRow>,
  CMxCommonApp.FilterDictionaryParams,
  { rejectValue: any }
>(Bench.FETCH_NOTE_TYPES, async (noteTypeParams) => {
  const currentDate = cmxDateTime.format(new Date(), cmxDateTime.FORMATS.DATE);
  const searchParams = new URLSearchParams({
    tenantId: noteTypeParams.processInstance.healthSystemTenantId.uuid,
    derivedIndicator: 'true',
    effectiveDate: currentDate,
    dictionaryName: 'Note Type',
    columnName: 'Key'
  });
  return fetchDictionaryRows<CMxCommonApp.NoteTypeRow>(searchParams);
});

const fetchRelateCaseFilterDictionaries = createAsyncThunk<
  {
    [key: string]:
      | CMxCommonApp.NoteTypeRow[]
      | CMxCommonApp.NoteSpecialtyRow[]
      | CMxCommonApp.PatientClassRow[];
  },
  CMxCommonApp.FilterDictionaryParams,
  { rejectValue: any }
>(
  Bench.FETCH_RELATED_CASE_FILTER_DICTIONARIES,
  async (noteTypeParams) => {
    const date_of_service =
      noteTypeParams.activeItem?.date_of_service ||
      cmxDateTime.format(new Date(), cmxDateTime.FORMATS.DATE);
    const currentDate = cmxDateTime.format(
      new Date(),
      cmxDateTime.FORMATS.DATE
    );
    const noteTypeSearchParams = new URLSearchParams({
      tenantId: noteTypeParams.processInstance.healthSystemTenantId.uuid,
      derivedIndicator: 'true',
      effectiveDate: currentDate,
      dictionaryName: 'Note Type',
      columnName: 'Key'
    });

    const noteTypes = await fetchDictionaryRows<CMxCommonApp.NoteTypeRow>(
      noteTypeSearchParams
    );

    const patientClassParams = new URLSearchParams({
      tenantId: noteTypeParams.processInstance.healthSystemTenantId.uuid,
      derivedIndicator: 'true',
      effectiveDate: date_of_service,
      dictionaryName: 'Patient Class',
      columnName: 'Key'
    });

    const patientClasses = await fetchDictionaryRows<
      CMxCommonApp.PatientClassRow
    >(patientClassParams);

    const noteSpecialtyParams = new URLSearchParams({
      tenantId: noteTypeParams.processInstance.healthSystemTenantId.uuid,
      derivedIndicator: 'true',
      effectiveDate: date_of_service,
      dictionaryName: 'Note Specialty',
      columnName: 'Key'
    });

    const noteSpecialties = await fetchDictionaryRows<
      CMxCommonApp.NoteSpecialtyRow
    >(noteSpecialtyParams);
    return {
      noteType: noteTypes?.rowValues,
      patientClass: patientClasses?.rowValues,
      noteSpecialty: noteSpecialties?.rowValues
    };
  }
);

const fetchProviderRoleTypeConfigurations = createAsyncThunk<
  Accelerate.DictionaryRowData<CMxCommonApp.ProviderRoleTypeConfigRow>,
  CMxCommonApp.ProviderRoleTypeParams,
  { rejectValue: any }
>(Bench.PROVIDER_ROLETYPE_CONFIGURATION, async (params) => {
  const date_of_service = cmxDateTime.format(
    new Date(),
    cmxDateTime.FORMATS.DATE
  );
  const searchParams = new URLSearchParams({
    tenantId: params.processInstance.healthSystemTenantId.uuid,
    derivedIndicator: 'true',
    effectiveDate: date_of_service,
    dictionaryName: 'Provider Role Type Configurations',
    columnName: 'Key'
  });

  return fetchDictionaryRows<CMxCommonApp.ProviderRoleTypeConfigRow>(
    searchParams
  );
});

const fetchOrderTypeStatus = createAsyncThunk<
  Accelerate.DictionaryRowData<CMxCommonApp.OrderTypeStatus>,
  CMxCommonApp.DictionaryInputParams,
  { rejectValue: any }
>(Bench.FETCH_ORDER_TYPE_STATUS, async (dictionaryInputParams) => {
  let currentDate = cmxDateTime.format(new Date(), cmxDateTime.FORMATS.DATE);
  const searchParams = new URLSearchParams({
    tenantId: dictionaryInputParams.processInstance.healthSystemTenantId.uuid,
    derivedIndicator: 'true',
    effectiveDate: currentDate,
    dictionaryName: 'HL7 Order Control Dictionary',
    columnName: 'Key'
  });

  return fetchDictionaryRows<CMxCommonApp.OrderTypeStatus>(searchParams);
});

const fetchCoderNotes = createAsyncThunk<
  AmplifyAPI.CoderNoteRow[],
  AmplifyAPI.CoderNoteInputParams,
  { rejectValue: any }
>(Bench.FETCH_CODER_NOTES, async (coderNoteInputParams) => {
  const { lookupType, lookupValue } = coderNoteInputParams;
  const lookupRefName = CoderNotesLookupTypeName[lookupType];
  const path = `/coding-api/coder_notes/v1/?${lookupRefName}=${lookupValue}`;
  const result = await api.requestJSON<AmplifyAPI.CoderNoteRow[]>(path, {
    method: HttpMethods.GET
  });
  return result;
});

const setLockCaseUid = createAction<string>(Bench.SET_LOCK_CASE_UID);

const acquireCaseAccess = createAsyncThunk<
  any,
  {
    case_uid: string;
    processInstance: AmplifyAPI.ProcessInstance;
    callback?: (result: {
      conflict?: boolean;
      success?: boolean;
      openedByName?: string;
    }) => void;
  },
  { rejectValue: any }
>(
  Bench.ACQUIRE_CASE_ACCESS,
  async ({ case_uid, processInstance, callback }, { dispatch }) => {
    try {
      const path = `/api/service-desk/directory-service/case_access/open/${case_uid}/v1`;
      const response = await api.request(path, {
        method: HttpMethods.PUT
      });

      if (response.status >= 200 && response.status < 300) {
        dispatch(setLockCaseUid(case_uid));
        dispatch(caseOpenSuccess(processInstance));
        if (callback) callback({ success: true });
      }
    } catch (error) {
      if (error?.response?.status === 409 || error?.status === 409) {
        dispatch(caseOpenConflict(processInstance));

        // Access response data directly based on the actual API response structure
        let openedByName: string | undefined;
        try {
          // The response data is now accessible via error.responseData
          const responseData = error.responseData || {};

          if (responseData?.opened_case_content) {
            const firstName =
              responseData.opened_case_content.opened_by_first_name;
            const lastName =
              responseData.opened_case_content.opened_by_last_name;
            const username =
              responseData.opened_case_content.opened_by_username;
            if (firstName && lastName) {
              openedByName = `${firstName} ${lastName}`;
            } else if (username) {
              openedByName = username;
            } else {
              openedByName = 'another user';
            }
          }
        } catch (e) {
          console.error('Failed to parse opened_by', e);
        }
        if (callback) callback({ conflict: true, openedByName });
      } else {
        console.error('case access open failed', error);
        dispatch(caseOpenFailed());
      }
    }
  }
);

const refreshCaseAccessTimer = createAsyncThunk<void, string, { rejectValue: any }>(
  Bench.REFRESH_CASE_ACCESS_TIMER,
  async (case_uid, { dispatch }) => {
    try {
      const path = `/api/service-desk/directory-service/case_access/open/${case_uid}/v1`;
      await api.request(path, {
        method: HttpMethods.PUT
      });
      dispatch(refreshTimerSuccess());
    } catch (error) {
      console.error('Timer refresh failed', error);
    }
  }
);

const releaseCaseAccess = createAsyncThunk<any, string, { rejectValue: any }>(
  Bench.RELEASE_CASE_ACCESS,
  async (case_uid, { dispatch }) => {
    try {
      const path = `/api/service-desk/directory-service/case_access/open/${case_uid}/v1`;
      const response = await api.request(path, {
        method: HttpMethods.DELETE
      });

      if (response.status === 204) {
        dispatch(caseCloseSuccess());
      }
    } catch (error) {
      const status = error?.response?.status ?? error?.status;
      if (status === 404 || error?.errors?.status === 'PAGE NOT FOUND') {
        dispatch(caseCloseNotFound());
      } else if (status === 409 || error?.response?.status === 409) {
        dispatch(caseCloseConflict());
      } else {
        console.error('case access close failed', error);
        dispatch(caseCloseFailed());
      }
    } finally {
      dispatch(setLockCaseUid(''));
    }
  }
);

const caseOpenSuccess = createAction<AmplifyAPI.ProcessInstance>(
  Bench.CASE_OPEN_SUCCESS
);
const caseOpenFailed = createAction<void>(Bench.CASE_OPEN_FAILED);
const caseOpenConflict = createAction<AmplifyAPI.ProcessInstance>(
  Bench.CASE_OPEN_CONFLICT
);
const refreshTimerSuccess = createAction<void>(Bench.REFRESH_TIMER_SUCCESS);
const caseCloseSuccess = createAction<void>(Bench.CASE_CLOSE_SUCCESS);
const caseCloseNotFound = createAction<void>(Bench.CASE_CLOSE_NOT_FOUND);
const caseCloseConflict = createAction<void>(Bench.CASE_CLOSE_CONFLICT);
const caseCloseFailed = createAction<void>(Bench.CASE_CLOSE_FAILED);
const resetCaseAccess = createAction<AmplifyAPI.ProcessInstance>(
  Bench.RESET_CASE_ACCESS
);

const setBenchMode = createAction<Accelerate.BenchMode>(Bench.SET_BENCH_MODE);

const addCoderNote = createAsyncThunk<
  AmplifyAPI.CoderNoteRow[],
  AmplifyAPI.CoderNoteInputParams,
  { rejectValue: any }
>(Bench.ADD_CODER_NOTE, async (coderNoteInputParams, { dispatch }) => {
  const {
    lookupType,
    lookupValue,
    noteContent,
    patientMRN
  } = coderNoteInputParams;
  const lookupRefName = CoderNotesLookupTypeName[lookupType];
  const path = `/coding-api/coder_notes/v1/?${lookupRefName}=${lookupValue}`;
  const result = await api.requestJSON<any>(path, {
    method: HttpMethods.POST,
    body: JSON.stringify({
      lookup_type: lookupType,
      note_content: noteContent,
      patient_mrn: patientMRN
    })
  });
  const action = await dispatch(fetchCoderNotes(coderNoteInputParams) as any);
  return action.payload;
});

const addUserActivityComment = createAsyncThunk<
  void,
  {processInstance:AmplifyAPI.ProcessInstance,comment:string},
  { rejectValue: any }
>(Bench.ADD_USER_ACTIVITY_COMMENT, async ({processInstance,comment}, { dispatch }) => {
  const path = `/coding-api/processinstance/${processInstance.serviceLine}/${processInstance.guid.uuid}/comments/v1`;
  await api.request(path, {
    method: HttpMethods.POST,
    body: JSON.stringify({
      comment
    })
  });
  return dispatch(fetchActivity(processInstance) as any) as any;
});


const updateCoderNote = createAsyncThunk<
  AmplifyAPI.CoderNoteRow[],
  AmplifyAPI.CoderNoteInputParams,
  { rejectValue: any }
>(Bench.UPDATE_CODER_NOTE, async (coderNoteInputParams, { dispatch }) => {
  const path = `/coding-api/coder_notes/${coderNoteInputParams?.noteId}/v1/`;
  const result = await api.requestJSON<any>(path, {
    method: HttpMethods.PUT,
    body: JSON.stringify({
      note_content: coderNoteInputParams?.noteContent,
      patient_mrn: coderNoteInputParams?.patientMRN
    })
  });
  const action = await dispatch(fetchCoderNotes(coderNoteInputParams) as any);
  return action.payload;
});

const deleteCoderNote = createAsyncThunk<
  AmplifyAPI.CoderNoteRow[],
  AmplifyAPI.CoderNoteInputParams,
  { rejectValue: any }
>(Bench.DELETE_CODER_NOTE, async (coderNoteInputParams, { dispatch }) => {
  const path = `/coding-api/coder_notes/${coderNoteInputParams?.noteId}/v1/`;
  const result = await api.request(path, {
    method: HttpMethods.DELETE
  });
  const action = await dispatch(fetchCoderNotes(coderNoteInputParams) as any);
  return action.payload;
});

const setCodeBenchPropertyConfig = createAction<
  CMxCommonApp.PatientInformationConfiguration
>('SET_CODE_BENCH_PROPERTY_CONFIG');

const refreshHoldSubmission = createAction<void>(
  Bench.REFRESH_HOLD_SUBMISSION
);

export {
  txnSharedCodeAction,
  navigateDetail,
  cancelInvoicesPopup,
  cancelInvoicesConfirm,
  cancelInvoicesDismiss,
  selectInvoiceToEdit,
  selectInvoiceToAdd,
  navigatePatient,
  navigateProvider,
  fetchProcessInstance,
  codeSearch,
  codeSearchDrop,
  codeUpdate,
  codeUpdateFlow,
  codeRemove,
  timeBasedCodeAdd,
  timeBasedCodeUpdate,
  codeAdd,
  createTransactionAndAddCode,
  submitInvoice,
  openAddChargeModal,
  closeAddChargeModal,
  addTransaction,
  showAddChargeLoading,
  showSubmittedFeedback,
  showLoadingSubmission,
  showLoadingCancelation,
  hideLoadingSubmission,
  hideSubmittedFeedback,
  showCanceledFeedback,
  submittedFeedbackTimer,
  showSizeExpWarning,
  hideSizeExpWarning,
  deleteSizeExpWarning,
  enableTechPro,
  disableTechPro,
  setLoadingTechPro,
  showvitalwareIssuesModal,
  hidevitalwareIssuesModal,
  setvitalwareIssues,
  fetchAllInvoices,
  hideHoldCaseModal,
  showHoldCaseModal,
  showExclusionCaseModal,
  showCaseCommentModal,
  hideCaseCommentModal,
  hideExclusionCaseModal,
  toggleLateCaseClearModal,
  clearLateCaseNotification,
  showSplitCaseModal,
  hideSplitCaseModal,
  toggleChargeableCase,
  holdCase,
  getHoldCaseForProcessInstance,
  getCaseVersions,
  toggleSubmitOldCaseModal,
  showSideNavBar,
  hideSideNavBar,
  loadNextOrPreviousCase,
  loadWorkitemInAmplify,
  loadCursorAPIContext,
  resetCursorAPIContext,
  openWindow,
  getCaseEntries,
  getPatientMessageCounts,
  getFlagItems,
  getCasesWithEncounter,
  getCCICustomClaimCrossCases,
  getDocumentsAsHtml,
  getDocuments,
  setFilterTypes,
  setFilterDates,
  loadRoles,
  fetchNoteTypes,
  updateTransaction,
  updateTransactionOptimistically,
  createTransactionOptimistically,
  replaceOptimisticTransaction,
  docMetadataAvailable,
  setActiveNote,
  setFilterDatesInfinite,
  setFilterTypesInfinite,
  getCaseEntriesWithFilter,
  getPatientMessageCountsWithFilter,
  fetchOrderTypeStatus,
  navigateCoderNotes,
  addCoderNote,
  fetchCoderNotes,
  updateCoderNote,
  deleteCoderNote,
  removeInvoice,
  getCodesWithEncounter,
  fetchRelatedCodes,
  getRelatedCodes,
  codesAdd,
  codesRemove,
  fetchProviderRoleTypeConfigurations,
  setCodeBenchPropertyConfig,
  fetchSourceInvoices,
  transactionOrderChange,
  fetchRelateCaseFilterDictionaries,
  updateRelatedCasesFilterSelection,
  acquireCaseAccess,
  releaseCaseAccess,
  refreshCaseAccessTimer,
  caseOpenSuccess,
  caseOpenConflict,
  caseCloseSuccess,
  setLockCaseUid,
  resetCaseAccess,
  setBenchMode,
  refreshHoldSubmission,
  addUserActivityComment,
  fetchExcludeCasePermissionsConfig
};
